import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { TEXT_SIZES, SCALE_DEFAULT, SCALE_MAX, SCALE_MIN, bodyFontSize, fontStack, headlineFontSize, loadFont } from '../lib/designTokens';
import { FontPicker } from '../components/design/FontPicker';
import { logger } from '../lib/logger';
import { prepareImageForUpload, looksLikeImage, uploadFailureMessage, uploadWithProgress } from '../utils/imageUpload';
import { RemoveBackgroundButton } from '../components/shared/RemoveBackgroundButton';
import { UploadProgress } from '../components/shared/UploadProgress';

import {
    Gift, Upload, DollarSign, Clock, MessageSquare,
    Plus, X, Loader2, Eye, Save, Image as ImageIcon,
    Users, MapPin, Trash2, Check, CreditCard, Mail, Building2, Phone,
    BarChart3, Settings, Wallet, Download, ChevronDown, ChevronRight,
    TrendingUp, CircleDollarSign, Coins, Activity, CalendarClock,
    Palette, AlertTriangle, ShieldAlert, Info, Gamepad2, Radio,
} from 'lucide-react';
import { VoucherAuditReports } from '../components/voucher_audit/VoucherAuditReports';
import { VoucherActivityLog } from '../components/voucher_audit/VoucherActivityLog';
import { RecipientListEditor } from '../components/shared/RecipientListEditor';
import { GiftSetupPicker } from '../components/voucher_settings/GiftSetupPicker';
import { AddToGoalGameButton, useGoalGameLink } from '../components/voucher_settings/AddToGoalGame';
import { BrandAssetPicker } from '../components/voucher_settings/BrandAssetPicker';
import VoucherRecipientsTab from './VoucherRecipientsTab';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { SkeletonList } from '../components/shared/Polish';
import { cardSurfaceClass } from '../components/shared/cardSurface';
import { stringifyDetail } from '../lib/utils';
import { parseUtc } from '../lib/datetime';

const API_BASE = window.location.origin;

// Centralized reason list with stripe color mapping (matches StaffMenu)
const VOUCHER_REASONS = [
    { code: 'late_food', labelKey: 'staffMenu.lateFoodReason', stripe: 'red', category: 'recovery' },
    { code: 'wrong_order', labelKey: 'staffMenu.wrongOrderReason', stripe: 'red', category: 'recovery' },
    { code: 'long_wait', labelKey: 'staffMenu.longWaitReason', stripe: 'red', category: 'recovery' },
    { code: 'manager_goodwill', labelKey: 'staffMenu.managerGoodwillReason', stripe: 'red', category: 'recovery' },
    { code: 'birthday', labelKey: 'staffMenu.birthdayReason', stripe: 'green', category: 'recognition' },
    { code: 'vip', labelKey: 'staffMenu.vipReason', stripe: 'gold', category: 'vip' },
    { code: 'other', labelKey: 'staffMenu.otherReason', stripe: 'green', category: 'recognition' },
];

const STRIPE_BUTTON_STYLES = {
    red: { bg: 'bg-red-500/10 border-red-500/40 hover:bg-red-500/20', text: 'text-red-600 dark:text-red-400', dot: 'bg-red-500', ring: 'ring-red-500/30' },
    green: { bg: 'bg-emerald-500/10 border-emerald-500/40 hover:bg-emerald-500/20', text: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', ring: 'ring-emerald-500/30' },
    gold: { bg: 'gold-gradient-bg-subtle gold-border gold-gradient-bg-hover', text: 'gold-gradient-text', dot: 'gold-gradient-dot', ring: 'gold-ring' },
    blue: { bg: 'bg-blue-500/10 border-blue-500/40 hover:bg-blue-500/20', text: 'text-blue-600 dark:text-blue-400', dot: 'bg-blue-500', ring: 'ring-blue-500/30' },
};

const STRIPE_PREVIEW_CLASSES = {
    red: { bar: 'bg-red-500', value: 'text-red-400', accent: 'text-red-400/80' },
    green: { bar: 'bg-emerald-500', value: 'text-emerald-400', accent: 'text-emerald-400/80' },
    gold: { bar: 'gold-gradient-bar', value: 'gold-gradient-text', accent: 'gold-gradient-text' },
    blue: { bar: 'bg-blue-500', value: 'text-blue-400', accent: 'text-blue-400/80' },
};

const fmtMoney = (v) => `$${Number(v || 0).toFixed(2)}`;

// ── Voucher Design Controls ──────────────────────────────────────────
// The generated PNG (services/voucher_artwork.py) honors three org-level
// colors: background, text, accent. Unset = today's exact look. "Classic"
// clears all three back to the default (reason-colored) appearance.
// Picker fall-back display values (used only when a field is unset) match
// the renderer defaults: text #E2E8F0, a dark background, and the reason
// accent. We surface neutral defaults for the swatch chips.
const VOUCHER_DESIGN_DEFAULTS = { bg: '#0B1218', text: '#E2E8F0', accent: '#10B981' };

const VOUCHER_DESIGN_PRESETS = [
    { key: 'classic', label: 'Classic', bg: '', text: '', accent: '', swatch: ['#0B1218', '#E2E8F0', '#10B981'] },
    { key: 'midnight', label: 'Midnight', bg: '#0B1220', text: '#E2E8F0', accent: '#3B82F6', swatch: ['#0B1220', '#E2E8F0', '#3B82F6'] },
    { key: 'ocean', label: 'Ocean', bg: '#0C2A3A', text: '#E6F4FA', accent: '#22D3EE', swatch: ['#0C2A3A', '#E6F4FA', '#22D3EE'] },
    { key: 'emerald', label: 'Emerald', bg: '#05231A', text: '#E7FBF3', accent: '#10B981', swatch: ['#05231A', '#E7FBF3', '#10B981'] },
    { key: 'burgundy', label: 'Burgundy', bg: '#2A0A12', text: '#FBE9EE', accent: '#E11D48', swatch: ['#2A0A12', '#FBE9EE', '#E11D48'] },
    { key: 'clean', label: 'Clean', bg: '#FFFFFF', text: '#0F172A', accent: '#2563EB', swatch: ['#FFFFFF', '#0F172A', '#2563EB'] },
];

const _hexToRgb = (hex) => {
    if (!hex) return null;
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6) return null;
    const n = parseInt(h, 16);
    if (Number.isNaN(n)) return null;
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const _relLuminance = ({ r, g, b }) => {
    const f = (c) => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};
// WCAG contrast ratio between two hex colors, or null if either is invalid.
const contrastRatio = (hexA, hexB) => {
    const a = _hexToRgb(hexA); const b = _hexToRgb(hexB);
    if (!a || !b) return null;
    const la = _relLuminance(a); const lb = _relLuminance(b);
    const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
    return (hi + 0.05) / (lo + 0.05);
};
const _lighten = (hex, amt = 0.08) => {
    const c = _hexToRgb(hex);
    if (!c) return hex;
    const m = (v) => Math.round(v + (255 - v) * amt);
    return `rgb(${m(c.r)}, ${m(c.g)}, ${m(c.b)})`;
};

export default function VoucherSettings({ embedded = false, scopeOrgId = '' }) {
    const { t, i18n } = useTranslation();
    const { user, hasPermission } = useAuth();
    const token = localStorage.getItem('yulontap_token');
    const fileRef = useRef(null);

    // Permission gates for Staff Incentives and Voucher Statistics
    const isSuperAdmin = user?.role === 'super_admin';
    const isFranchiseAdmin = user?.role === 'franchise_admin';
    const isClientAdmin = user?.role === 'client_admin';
    const isElevated = isSuperAdmin || isFranchiseAdmin;
    const isAuthorizedMgr = isSuperAdmin || isFranchiseAdmin || isClientAdmin;
    const canManageIncentives = isAuthorizedMgr || hasPermission('view_management_audit');
    const canViewStatistics = isAuthorizedMgr || hasPermission('view_voucher_reports') || hasPermission('view_management_audit');

    const [activeTab, setActiveTab] = useState('settings');

    // v4.45 — Loyal-Review source vouchers, loaded on demand when the
    // Recipients tab is opened. Populates the "This Voucher" scope picker.
    const [lrSourceVouchers, setLrSourceVouchers] = useState([]);

    // v4.45.1 — Deep-link support. When the URL carries
    // `?interaction_id=…&voucher_source_id=…` (from the Operational
    // Base Email Modal), auto-open the Recipients tab, pre-select the
    // right source voucher, and pass through the interaction id so
    // that the child component can scroll + highlight the matching row.
    const [deepLinkParams, setDeepLinkParams] = useState(null);
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const interactionId = params.get('interaction_id');
        const sourceId = params.get('voucher_source_id');
        const voucherCode = params.get('voucher_code');
        if (interactionId || sourceId || voucherCode) {
            setActiveTab('recipients');
            setDeepLinkParams({ interactionId, sourceId, voucherCode });
        }
    }, []);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    // Per-asset upload feedback: key -> { pct, preparing, preview }. The local
    // blob preview shows the picked image INSTANTLY, before the bytes land.
    const [uploadState, setUploadState] = useState({});
    const beginUpload = (key, file) => setUploadState((s) => ({
        ...s, [key]: { pct: 0, preparing: true, preview: URL.createObjectURL(file) },
    }));
    const trackUpload = (key) => (pct) => setUploadState((s) => (
        s[key] ? { ...s, [key]: { ...s[key], pct, preparing: false } } : s));
    const endUpload = (key) => setUploadState((s) => {
        const next = { ...s };
        const blob = next[key]?.preview;
        if (blob) setTimeout(() => URL.revokeObjectURL(blob), 2000);
        delete next[key];
        return next;
    });
    /** Local blob while uploading, else the stored asset URL. */
    const assetSrc = (key, url) => uploadState[key]?.preview
        || (url ? (/^(blob:|https?:)/.test(url) ? url : `${API_BASE}${url}`) : null);

    // Settings state
    const [logoUrl, setLogoUrl] = useState(null);
    const [logoSize, setLogoSize] = useState(40);
    const [cardStyle, setCardStyle] = useState('frosted');
    // Voucher Design Controls — org-level colors honored by the PNG renderer.
    // Empty string = unset (renderer uses today's default look).
    const [voucherBg, setVoucherBg] = useState('');
    const [voucherText, setVoucherText] = useState('');
    const [voucherAccent, setVoucherAccent] = useState('');
    const [presetAmounts, setPresetAmounts] = useState([20, 25, 50, 75]);
    const [presetExpiry, setPresetExpiry] = useState([30, 60, 90]);
    const [defaultExpiry, setDefaultExpiry] = useState(30);
    const [defaultMessage, setDefaultMessage] = useState('');
    const [newAmount, setNewAmount] = useState('');
    const [newExpiry, setNewExpiry] = useState('');

    // ── Gift Generator state (extends existing voucher settings) ──
    const [ggEnabled, setGgEnabled] = useState(false);
    // Voucher Allocation starts collapsed in Game Mode only.
    const [allocExpanded, setAllocExpanded] = useState(false);
    // Loyal Review download-spike alerting (org-scoped).
    const [spikeThreshold, setSpikeThreshold] = useState(10);
    const [spikeEmails, setSpikeEmails] = useState([]);
    const [spikeEmailDraft, setSpikeEmailDraft] = useState('');
    const [spikePhones, setSpikePhones] = useState([]);
    const [spikePhonesOk, setSpikePhonesOk] = useState(true);
    const [ggExchangeEnabled, setGgExchangeEnabled] = useState(false);
    const [ggExchangeLimit, setGgExchangeLimit] = useState(2);
    const [ggRevealMethod, setGgRevealMethod] = useState('button');
    const [ggLeadGenEnabled, setGgLeadGenEnabled] = useState(false);
    // Per-gift expansion of the doubled-prize block while Lead Generation is OFF.
    const [dblExpanded, setDblExpanded] = useState({});    const [ggLeadGenUrl, setGgLeadGenUrl] = useState('');
    const [ggMode, setGgMode] = useState('game');
    // Gift Generator in Game Mode: Voucher Allocation plays no part, so it is
    // collapsed behind one tap. Everything inside is unchanged once opened.
    const allocCollapsible = ggEnabled && ggMode === 'game';
    const allocOpen = !allocCollapsible || allocExpanded;
    const [ggLogoSizePx, setGgLogoSizePx] = useState(96);
    // Per-setup MERCHANT logo override (not the org logo, not the voucher logo).
    const [ggMerchantLogoUrl, setGgMerchantLogoUrl] = useState(null);
    const [ggMerchantLogoUploading, setGgMerchantLogoUploading] = useState(false);
    const [ggCopy, setGgCopy] = useState({});
    const [ggCopyLang, setGgCopyLang] = useState('en');
    const [ggGifts, setGgGifts] = useState([]);
    // Gift Generator — guest wallpaper + gift-voucher design mode
    const [ggWallpaperUrl, setGgWallpaperUrl] = useState(null);
    const [ggWallpaperUploading, setGgWallpaperUploading] = useState(false);
    // Optional logo painted on the scratch panel (scratch reveal method only)
    const [ggScratchLogoUrl, setGgScratchLogoUrl] = useState(null);
    const [ggScratchLogoUploading, setGgScratchLogoUploading] = useState(false);
    const [ggScratchLogoOpacity, setGgScratchLogoOpacity] = useState(30);
    const [ggScratchLogoSize, setGgScratchLogoSize] = useState(38);
    // Named Gift Generator setups. The picker edits ONE setup at a time; the
    // whole Gift Generator form below it belongs to the selected setup.
    const [ggSetups, setGgSetups] = useState([]);
    const [ggSetupId, setGgSetupId] = useState('');
    const [giftSaving, setGiftSaving] = useState({});
    const [ggSetupBusy, setGgSetupBusy] = useState(false);
    const [ggVoucherDesignMode, setGgVoucherDesignMode] = useState('existing');
    const [ggVoucherBg, setGgVoucherBg] = useState('');
    const [ggVoucherText, setGgVoucherText] = useState('');
    const [ggVoucherAccent, setGgVoucherAccent] = useState('');
    // Shared design fields (same saved keys Tap’N Win reads). '' = unset,
    // which renders exactly as this setup did before these controls existed.
    const [ggHeadlineFont, setGgHeadlineFont] = useState('');
    const [ggBodyFont, setGgBodyFont] = useState('');
    const [ggHeadlineSize, setGgHeadlineSize] = useState('');
    const [ggBodySize, setGgBodySize] = useState('');
    // The fine control. 100 = exactly as the step renders, which is also what an
    // untouched setup sends, so nothing saved renders unchanged.
    const [ggHeadlineScale, setGgHeadlineScale] = useState(SCALE_DEFAULT);
    const [ggBodyScale, setGgBodyScale] = useState(SCALE_DEFAULT);

    // The preview needs the saved faces, not only the ones picked in this visit.
    useEffect(() => {
        loadFont(ggHeadlineFont);
        loadFont(ggBodyFont);
    }, [ggHeadlineFont, ggBodyFont]);
    const [ggVoucherButton, setGgVoucherButton] = useState('');

    // Preview selection state
    const [previewAmount, setPreviewAmount] = useState(null);
    const [previewExpiry, setPreviewExpiry] = useState(null);
    const [previewReason, setPreviewReason] = useState('other');

    // Allocation state
    const [organizations, setOrganizations] = useState([]);
    const [allLocations, setAllLocations] = useState([]);
    const [allocOrg, setAllocOrg] = useState('');
    const [recipients, setRecipients] = useState([]);
    const [allocLocation, setAllocLocation] = useState('');
    const [allocDeliveryMethod, setAllocDeliveryMethod] = useState('staff_card');
    const [allocRecipient, setAllocRecipient] = useState('');
    const [allocEmail, setAllocEmail] = useState('');
    const [allocPhone, setAllocPhone] = useState('');
    const [allocAmount, setAllocAmount] = useState('');
    const [allocCustomAmount, setAllocCustomAmount] = useState('');
    const [allocReason, setAllocReason] = useState('other');
    const [allocQty, setAllocQty] = useState(1);
    const [allocAvailableForLoyalReview, setAllocAvailableForLoyalReview] = useState(false);
    const [allocList, setAllocList] = useState([]);
    const [allocConfirmOpen, setAllocConfirmOpen] = useState(false);
    const [allocating, setAllocating] = useState(false);
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [maxVoucherValue, setMaxVoucherValue] = useState(100);

    // ── Staff Incentives state ──
    const [incentiveEnabled, setIncentiveEnabled] = useState(false);
    const [incentiveRate, setIncentiveRate] = useState(0);
    const [incentiveSaving, setIncentiveSaving] = useState(false);
    const [incentiveOrgId, setIncentiveOrgId] = useState('');
    const [settingsOrgId, setSettingsOrgId] = useState('');
    // Tap’N Win link: which promotions this organization has and which
    // gifts are already prizes. Absent for an org without Tap’N Win.
    // Tap’N Win button on a gift is gated by the GOAL GAME, never by the
    // Gift Generator switch: a venue that runs Tap’N Win always sees it.
    const goalLink = useGoalGameLink(settingsOrgId, true);
    const [staffOverview, setStaffOverview] = useState([]);
    const [staffLoading, setStaffLoading] = useState(false);
    const [redeemTarget, setRedeemTarget] = useState(null);
    const [redeemAmount, setRedeemAmount] = useState('');
    const [redeeming, setRedeeming] = useState(false);

    // ── Voucher Statistics state ──
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [fStart, setFStart] = useState('');
    const [fEnd, setFEnd] = useState('');
    const [fLocation, setFLocation] = useState('all');
    const [fEmployee, setFEmployee] = useState('all');
    const [fReason, setFReason] = useState('all');
    const [expandedLocs, setExpandedLocs] = useState({});

    // Derive filtered locations from selected org
    const locations = allocOrg
        ? allLocations.filter(loc => loc.organization_id === allocOrg)
        : allLocations;

    // v4.34.2 — `headers` was previously rebuilt on every render which made
    // every hook that referenced it from closure look "non-deterministic" to
    // ESLint's exhaustive-deps. Memoising keyed on `token` gives a stable
    // identity for the lifetime of the session and lets dependent hooks
    // declare it honestly.
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    // Fill the Gift Generator form from ONE setup's config. Shared by the
    // initial load and by switching setups, so the two can never diverge.
    const applyGgConfig = useCallback((gg, fallbackLogoSize) => {
        setGgEnabled(!!gg.enabled);
        setGgExchangeEnabled(!!gg.exchange_enabled);
        setGgExchangeLimit(Number(gg.exchange_limit ?? 2));
        setGgRevealMethod(gg.reveal_method === 'scratch' ? 'scratch' : 'button');
        setGgLeadGenEnabled(!!gg.lead_generation_enabled);
        setGgLeadGenUrl(gg.lead_generation_url || '');
        setGgMode(gg.gift_generator_mode === 'review' ? 'review' : 'game');
        setGgLogoSizePx(Math.max(20, Math.min(240, Number(gg.logo_size_px) || Number(fallbackLogoSize) || 96)));
        setGgCopy(gg.copy && typeof gg.copy === 'object' ? gg.copy : {});
        setGgWallpaperUrl(gg.wallpaper_url || null);
        setGgMerchantLogoUrl(gg.merchant_logo_url || null);
        setGgScratchLogoUrl(gg.scratch_surface_logo_url || null);
        setGgScratchLogoOpacity(Math.max(15, Math.min(60, Number(gg.scratch_surface_logo_opacity) || 30)));
        setGgScratchLogoSize(Math.max(25, Math.min(100, Number(gg.scratch_surface_logo_size) || 38)));
        setGgVoucherDesignMode(gg.voucher_design_mode === 'custom' ? 'custom' : 'existing');
        setGgVoucherBg(gg.gg_voucher_bg || '');
        setGgVoucherText(gg.gg_voucher_text || '');
        setGgVoucherAccent(gg.gg_voucher_accent || '');
        setGgHeadlineFont(gg.headline_font || '');
        setGgBodyFont(gg.body_font || '');
        setGgHeadlineSize(gg.headline_size || '');
        setGgBodySize(gg.body_size || '');
        setGgHeadlineScale(gg.headline_scale || SCALE_DEFAULT);
        setGgBodyScale(gg.body_scale || SCALE_DEFAULT);
        setGgVoucherButton(gg.gg_voucher_button || '');
        setGgGifts((gg.gifts || []).map(g => ({
            id: g.id,
            title: g.title || '',
            description: g.description || '',
            gift_type: g.gift_type === 'monetary' ? 'monetary' : 'non_monetary',
            value: g.value || 0,
            quantity: g.quantity ?? 0,
            remaining: g.remaining,
            image_url: g.image_url || null,
            image_path: g.image_path || null,
            double_image_url: g.double_image_url || null,
            double_image_path: g.double_image_path || null,
        })));
    }, []);

    const setupsQs = useCallback(
        () => (settingsOrgId ? `?organization_id=${settingsOrgId}` : ''), [settingsOrgId]);

    const loadGgSetups = useCallback(async (selectId) => {
        try {
            const res = await fetch(`${API_BASE}/api/voucher-settings/gift-setups${setupsQs()}`, { headers });
            if (!res.ok) return [];
            const list = (await res.json()).setups || [];
            setGgSetups(list);
            setGgSetupId(prev => {
                const want = selectId || prev;
                if (want && list.some(s => s.id === want)) return want;
                return (list.find(s => s.is_default) || list[0] || {}).id || '';
            });
            return list;
        } catch { return []; }
    }, [headers, setupsQs]);

    const loadGgSetupConfig = useCallback(async (id) => {
        if (!id) return;
        setGgSetupBusy(true);
        try {
            const res = await fetch(
                `${API_BASE}/api/voucher-settings/gift-setups/${id}${setupsQs()}`, { headers });
            if (res.ok) applyGgConfig((await res.json()).config || {}, logoSize);
        } finally { setGgSetupBusy(false); }
    }, [headers, setupsQs, applyGgConfig, logoSize]);

    const selectGgSetup = async (id) => {
        setGgSetupId(id);
        await loadGgSetupConfig(id);
    };

    const ggSetupAction = async (path, options, successKey, selectId) => {
        setGgSetupBusy(true);
        try {
            const res = await fetch(
                `${API_BASE}/api/voucher-settings/gift-setups${path}${setupsQs()}`, {
                    ...options, headers: { ...headers, 'Content-Type': 'application/json' },
                });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const d = data.detail;
                if (d && typeof d === 'object' && Array.isArray(d.cards)) {
                    toast.error(t('giftSetups.deleteBlocked', { cards: d.cards.join(', ') }));
                } else {
                    toast.error(stringifyDetail(d) || t('giftSetups.actionFailed'));
                }
                return false;
            }
            const nextId = selectId === 'fromResponse' ? data.id : selectId;
            await loadGgSetups(nextId);
            if (nextId) await loadGgSetupConfig(nextId);
            toast.success(t(successKey));
            return true;
        } catch {
            toast.error(t('vouchers.connectionError'));
            return false;
        } finally { setGgSetupBusy(false); }
    };

    const createGgSetup = (name) => ggSetupAction('', {
        method: 'POST', body: JSON.stringify({ name }),
    }, 'giftSetups.created', 'fromResponse');

    const duplicateGgSetup = () => ggSetupAction('', {
        method: 'POST', body: JSON.stringify({ copy_from_setup_id: ggSetupId }),
    }, 'giftSetups.duplicated', 'fromResponse');

    const renameGgSetup = (name) => ggSetupAction(`/${ggSetupId}`, {
        method: 'PATCH', body: JSON.stringify({ name }),
    }, 'giftSetups.renamed', ggSetupId);

    const setGgSetupDefault = () => ggSetupAction(`/${ggSetupId}/default`, {
        method: 'POST',
    }, 'giftSetups.defaultChanged', ggSetupId);

    const deleteGgSetup = () => ggSetupAction(`/${ggSetupId}`, {
        method: 'DELETE',
    }, 'giftSetups.deleted', null);

    /** SAVE ONE GIFT, on its own, into the organization and setup shown above.
     *  No "Save Settings" at the top, nothing else on the page can overwrite
     *  it, and a gift is never dropped without a message. */
    const saveGift = async (idx) => {
        const gift = ggGifts[idx];
        if (!settingsOrgId) { toast.error(t('giftGenerator.selectOrgFirst')); return; }
        if (!ggSetupId) { toast.error(t('giftGenerator.selectSetupFirst')); return; }
        if (!(gift.title || '').trim()) { toast.error(t('giftGenerator.giftNameRequired')); return; }
        setGiftSaving((prev) => ({ ...prev, [idx]: true }));
        try {
            const res = await fetch(
                `${API_BASE}/api/voucher-settings/gift-setups/${ggSetupId}/gifts?organization_id=${settingsOrgId}`,
                {
                    method: 'POST',
                    headers: { ...headers, 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: gift.id || null,
                        title: (gift.title || '').trim(),
                        description: gift.description || '',
                        gift_type: gift.gift_type === 'monetary' ? 'monetary' : 'non_monetary',
                        value: gift.gift_type === 'monetary' ? (Number(gift.value) || 0) : 0,
                        quantity: Number(gift.quantity) || 0,
                        image_url: gift.image_url || null,
                        image_path: gift.image_path || null,
                    }),
                });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const reasons = {
                    gift_name_required: t('giftGenerator.giftNameRequired'),
                    select_organization_first: t('giftGenerator.selectOrgFirst'),
                    setup_not_found: t('giftGenerator.selectSetupFirst'),
                    gift_limit_reached: t('giftGenerator.giftLimitReached'),
                };
                toast.error(reasons[data.detail] || stringifyDetail(data.detail)
                            || t('giftGenerator.giftSaveFailed'));
                return;
            }
            // Keep the id the server gave it, so the next save edits this gift.
            setGgGifts((prev) => prev.map((g, i) => (
                i === idx ? { ...g, id: data.gift.id, remaining: data.gift.remaining } : g)));
            await loadGgSetups(ggSetupId);
            await goalLink.reload();
            toast.success(t('giftGenerator.giftSaved'));
        } catch {
            toast.error(t('vouchers.connectionError'));
        } finally {
            setGiftSaving((prev) => ({ ...prev, [idx]: false }));
        }
    };

    const loadSettings = useCallback(async () => {
        try {
            const qs = settingsOrgId ? `?organization_id=${settingsOrgId}` : '';
            const res = await fetch(`${API_BASE}/api/voucher-settings${qs}`, { headers });
            if (res.ok) {
                const data = await res.json();
                const amounts = data.preset_amounts || [20, 25, 50, 75];
                const expDays = data.default_expiry_days || 30;
                setPresetAmounts(amounts);
                setPresetExpiry(data.preset_expiry_days || [30, 60, 90]);
                setDefaultExpiry(expDays);
                setDefaultMessage(data.default_message || '');
                setLogoUrl(data.logo_url || null);
                setLogoSize(data.logo_size || 40);
                setCardStyle(['frosted', 'clear', 'solid'].includes(data.card_style) ? data.card_style : 'frosted');
                setSpikeThreshold(data.download_spike_threshold ?? 10);
                setSpikeEmails(Array.isArray(data.spike_alert_emails) ? data.spike_alert_emails : []);
                setSpikePhones(Array.isArray(data.spike_alert_phones) ? data.spike_alert_phones : []);
                setVoucherBg(data.voucher_bg || '');
                setVoucherText(data.voucher_text || '');
                setVoucherAccent(data.voucher_accent || '');
                // Gift Generator config — inventory (remaining) is server-owned.
                applyGgConfig(data.gift_generator || {}, data.logo_size);
                // Functional updates avoid capturing previewAmount/previewExpiry
                // in the dependency array, which would otherwise re-create the
                // callback every time the user changes a preview selection.
                if (amounts.length > 0) setPreviewAmount(prev => prev ?? amounts[0]);
                setPreviewExpiry(prev => prev ?? expDays);
            }
        } catch (e) {
            console.error('[VoucherSettings] loadSettings failed', e);
        } finally {
            setLoading(false);
        }
    }, [headers, settingsOrgId, applyGgConfig]);

    useEffect(() => { loadSettings(); }, [loadSettings]);
    useEffect(() => { loadGgSetups(); }, [loadGgSetups]);

    // ARRIVING FROM ANOTHER PAGE — the venue (and the Gift Generator setup) the
    // link carries wins, so a gift is never typed into the wrong venue. This
    // runs once, and only when the host surface is not pinning a venue itself.
    const urlOrgRef = useRef(false);
    useEffect(() => {
        if (urlOrgRef.current || scopeOrgId) return;
        const search = new URLSearchParams(window.location.search);
        const wantOrg = search.get('organization_id') || '';
        const wantSetup = search.get('setup_id') || '';
        if (!wantOrg) return;
        urlOrgRef.current = true;
        setAllocOrg(wantOrg);
        setIncentiveOrgId(wantOrg);
        setSettingsOrgId(wantOrg);
        if (wantSetup) setGgSetupId(wantSetup);
    }, [scopeOrgId]);

    // Load locations and organizations on mount + whenever the active user /
    // org context changes. Re-runs are bounded to one fetch per real change
    // because `headers` is memoised by token.
    useEffect(() => {
        fetch(`${API_BASE}/api/locations`, { headers })
            .then(r => r.ok ? r.json() : [])
            .then(data => {
                setAllLocations(data);
                if (!isSuperAdmin && user?.organization_id) {
                    setAllocOrg(user.organization_id);
                    setIncentiveOrgId(user.organization_id);
                    setSettingsOrgId(user.organization_id);
                }
            })
            .catch((e) => { logger.warn('[VoucherSettings] locations load failed', e); });
        fetch(`${API_BASE}/api/organizations`, { headers })
            .then(r => r.ok ? r.json() : [])
            .then(data => {
                setOrganizations(sortByName(data));
                if (data.length === 1) {
                    setAllocOrg(data[0].id);
                    setIncentiveOrgId(data[0].id);
                    setSettingsOrgId(prev => prev || data[0].id);
                    setMaxVoucherValue(data[0].max_voucher_value || 100);
                } else if (data.length > 1) {
                    // Super admin with multiple orgs — default tabs to first org (VISIBLY selected)
                    setIncentiveOrgId(data[0].id);
                    setSettingsOrgId(prev => prev || data[0].id);
                }
            })
            .catch((e) => { logger.warn('[VoucherSettings] organizations load failed', e); });
    }, [headers, isSuperAdmin, user?.organization_id]);

    // SCOPED EMBED — when the host surface (the Command Center) is pinned to one
    // organization, Vouchers follows it instead of defaulting to the first
    // organization in the list. Declared last so it wins over the loaders above.
    useEffect(() => {
        if (!scopeOrgId) return;
        setAllocOrg(scopeOrgId);
        setIncentiveOrgId(scopeOrgId);
        setSettingsOrgId(scopeOrgId);
    }, [scopeOrgId, organizations]);

    // Load recipients when location changes
    useEffect(() => {
        if (!allocLocation) { setRecipients([]); return; }
        setLoadingRecipients(true);
        fetch(`${API_BASE}/api/vouchers/recipients?location_id=${allocLocation}`, { headers })
            .then(r => r.ok ? r.json() : [])
            .then(data => { setRecipients(data); setAllocRecipient(''); })
            .catch((e) => { logger.warn('[VoucherSettings] recipients load failed', e); })
            .finally(() => setLoadingRecipients(false));
    }, [allocLocation, headers]);

    // ── Staff Incentives loaders ──
    const loadIncentiveSettings = useCallback(async () => {
        if (!canManageIncentives) return;
        const orgId = incentiveOrgId || user?.organization_id;
        if (!orgId) return;
        try {
            const res = await fetch(`${API_BASE}/api/organizations/${orgId}/incentive-settings`, { headers });
            if (res.ok) {
                const data = await res.json();
                setIncentiveEnabled(!!data.incentive_enabled);
                setIncentiveRate(Number(data.incentive_rate_per_tap || 0));
            }
        } catch (e) {
            console.error('[VoucherSettings] loadIncentiveSettings failed', e);
        }
    }, [incentiveOrgId, user?.organization_id, canManageIncentives, headers]);

    const loadStaffOverview = useCallback(async () => {
        if (!canManageIncentives) return;
        setStaffLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/management/staff-incentives`, { headers });
            if (res.ok) {
                const data = await res.json();
                setStaffOverview(data.staff || []);
            }
        } catch (e) {
            console.error('[VoucherSettings] loadStaffOverview failed', e);
            toast.error(t('vouchers.failedLoadOverview'));
        }
        finally { setStaffLoading(false); }
    }, [canManageIncentives, headers, t]);

    useEffect(() => {
        if (activeTab === 'incentives') {
            loadIncentiveSettings();
            loadStaffOverview();
        }
    }, [activeTab, loadIncentiveSettings, loadStaffOverview]);

    // ── Voucher Statistics loader ──
    const loadStatistics = useCallback(async () => {
        if (!canViewStatistics) return;
        setStatsLoading(true);
        try {
            const params = new URLSearchParams();
            if (fStart) params.set('start_date', fStart);
            if (fEnd) params.set('end_date', fEnd);
            if (fLocation !== 'all') params.set('location_id', fLocation);
            if (fEmployee !== 'all') params.set('employee_id', fEmployee);
            if (fReason !== 'all') params.set('reason_code', fReason);
            const res = await fetch(`${API_BASE}/api/vouchers/statistics?${params.toString()}`, { headers });
            if (res.ok) {
                const data = await res.json();
                setStats(data);
            } else {
                toast.error(t('vouchers.failedLoadStats'));
            }
        } catch (e) {
            console.error('[VoucherSettings] loadStatistics failed', e);
            toast.error(t('vouchers.connectionErr'));
        }
        finally { setStatsLoading(false); }
    }, [fStart, fEnd, fLocation, fEmployee, fReason, canViewStatistics, headers, t]);

    useEffect(() => {
        if (activeTab === 'statistics') loadStatistics();
    }, [activeTab, loadStatistics]);

    // v4.45 — Load LR source vouchers when the Recipients tab is opened.
    // Uses the existing /vouchers/loyal-review-available endpoint —
    // no new endpoint required.
    useEffect(() => {
        if (activeTab !== 'recipients') return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/api/vouchers/loyal-review-available`, { headers });
                if (!res.ok) return;
                const data = await res.json();
                if (cancelled) return;
                const items = (data.items || []).map(v => ({
                    id: v.id,
                    label: `$${v.value || '—'} ${v.reason_code || ''}`.trim(),
                    value: v.value,
                    reason_code: v.reason_code,
                }));
                setLrSourceVouchers(items);
            } catch (e) {
                logger.warn('[VoucherSettings] LR source vouchers load failed', e);
            }
        })();
        return () => { cancelled = true; };
    }, [activeTab, headers]);

    const saveIncentiveSettings = async () => {
        const orgId = incentiveOrgId || user?.organization_id;
        if (!orgId) { toast.error(t('vouchers.noOrgSelected')); return; }
        setIncentiveSaving(true);
        try {
            const res = await fetch(`${API_BASE}/api/organizations/${orgId}/incentive-settings`, {
                method: 'PUT',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    incentive_enabled: incentiveEnabled,
                    incentive_rate_per_tap: Number(incentiveRate) || 0,
                }),
            });
            if (res.ok) {
                toast.success(t('vouchers.settingsSaved2'));
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(stringifyDetail(err.detail) || t('vouchers.failedSave'));
            }
        } catch { toast.error(t('vouchers.connectionErr')); }
        finally { setIncentiveSaving(false); }
    };

    const openRedeemModal = (staff) => {
        setRedeemTarget(staff);
        setRedeemAmount(staff.incentive_balance ? String(staff.incentive_balance) : '');
    };

    const confirmRedeem = async () => {
        if (!redeemTarget) return;
        const amount = parseFloat(redeemAmount);
        if (!amount || amount <= 0) { toast.error(t('vouchers.enterValidAmount')); return; }
        if (amount > redeemTarget.incentive_balance) {
            toast.error(t('vouchers.exceedsBalance', { max: fmtMoney(redeemTarget.incentive_balance) }));
            return;
        }
        setRedeeming(true);
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/redeem-staff/${redeemTarget.employee_id}`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ amount }),
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(t('vouchers.voucherBluePaid', { code: data.code, amount: fmtMoney(data.value), name: data.employee_name }));
                setRedeemTarget(null);
                setRedeemAmount('');
                loadStaffOverview();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(stringifyDetail(err.detail) || t('manager.failed'));
            }
        } catch { toast.error(t('vouchers.connectionErr')); }
        finally { setRedeeming(false); }
    };

    const handleStatsExport = async (format) => {
        try {
            const params = new URLSearchParams({ format });
            if (fStart) params.set('start_date', fStart);
            if (fEnd) params.set('end_date', fEnd);
            if (fLocation !== 'all') params.set('location_id', fLocation);
            if (fEmployee !== 'all') params.set('employee_id', fEmployee);
            if (fReason !== 'all') params.set('reason_code', fReason);
            const res = await fetch(`${API_BASE}/api/vouchers/statistics/export?${params.toString()}`, {
                headers: { ...headers, 'Accept-Language': (localStorage.getItem('i18nextLng') || 'en').split('-')[0] },
            });
            if (!res.ok) { toast.error(t('vouchers.exportFailed')); return; }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `voucher-statistics.${format}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(t('vouchers.exportedFormat', { format: format.toUpperCase() }));
        } catch { toast.error(t('vouchers.exportFailed')); }
    };

    const saveSettings = async () => {
        // Defence in depth: the disabled attribute can be bypassed by an
        // Enter-submit or a programmatic click, and the server would then
        // silently drop the bad number — the exact thing we must prevent.
        if (!spikePhonesOk) {
            toast.error(t('phone.fixBeforeSave', 'Fix the highlighted phone number before saving.'));
            return;
        }
        setSaving(true);
        try {
            const qs = settingsOrgId ? `?organization_id=${settingsOrgId}` : '';
            const res = await fetch(`${API_BASE}/api/voucher-settings${qs}`, {
                method: 'PUT',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    preset_amounts: presetAmounts,
                    preset_expiry_days: presetExpiry,
                    default_expiry_days: defaultExpiry,
                    default_message: defaultMessage.trim() || null,
                    logo_size: logoSize,
                    // Voucher Design colors — empty string clears (renderer
                    // reverts to the default look for that field).
                    voucher_bg: voucherBg || '',
                    voucher_text: voucherText || '',
                    voucher_accent: voucherAccent || '',
                    card_style: cardStyle,
                    download_spike_threshold: Number(spikeThreshold) || 0,
                    spike_alert_emails: spikeEmails,
                    spike_alert_phones: spikePhones.filter((r) => (r.phone || '').trim()),
                    gift_generator_setup_id: ggSetupId || null,
                    gift_generator: {
                        enabled: ggEnabled,
                        exchange_enabled: ggExchangeEnabled,
                        exchange_limit: Number(ggExchangeLimit) || 0,
                        reveal_method: ggRevealMethod === 'scratch' ? 'scratch' : 'button',
                        lead_generation_enabled: !!ggLeadGenEnabled,
                        lead_generation_url: (ggLeadGenUrl || '').trim(),
                        gift_generator_mode: ggMode === 'review' ? 'review' : 'game',
                        logo_size_px: ggLogoSizePx,
                        copy: ggCopy,
                        wallpaper_url: ggWallpaperUrl || '',
                        merchant_logo_url: ggMerchantLogoUrl || '',
                        scratch_surface_logo_url: ggScratchLogoUrl || '',
                        scratch_surface_logo_opacity: Number(ggScratchLogoOpacity) || 30,
                        scratch_surface_logo_size: Number(ggScratchLogoSize) || 38,
                        voucher_design_mode: ggVoucherDesignMode === 'custom' ? 'custom' : 'existing',
                        gg_voucher_bg: ggVoucherBg || '',
                        gg_voucher_text: ggVoucherText || '',
                        gg_voucher_accent: ggVoucherAccent || '',
                        headline_font: ggHeadlineFont || '',
                        body_font: ggBodyFont || '',
                        headline_size: ggHeadlineSize || '',
                        body_size: ggBodySize || '',
                        headline_scale: ggHeadlineScale,
                        body_scale: ggBodyScale,
                        gg_voucher_button: ggVoucherButton || '',
                        gifts: ggGifts
                            .filter(g => (g.title || '').trim())
                            .map(g => ({
                                id: g.id,
                                title: g.title.trim(),
                                description: g.description || '',
                                gift_type: g.gift_type === 'monetary' ? 'monetary' : 'non_monetary',
                                value: g.gift_type === 'monetary' ? (Number(g.value) || 0) : 0,
                                quantity: Number(g.quantity) || 0,
                                image_url: g.image_url || null,
                                image_path: g.image_path || null,
                                double_image_url: g.double_image_url || null,
                                double_image_path: g.double_image_path || null,
                            })),
                    },
                }),
            });
            if (res.ok) {
                toast.success(t('vouchers.settingsSaved'));
                // Sequential: loadSettings re-applies the DEFAULT setup's
                // config (the mirror), so the selected setup has to be
                // re-applied after it, not alongside it.
                await loadSettings();
                await loadGgSetups(ggSetupId);
                await loadGgSetupConfig(ggSetupId);
            } else {
                toast.error(t('vouchers.settingsSaveFailed'));
            }
        } catch {
            toast.error(t('vouchers.connectionError'));
        } finally {
            setSaving(false);
        }
    };

    /** A logo whose transparency was flattened upstream (the iPad Photos picker
        transcodes to JPEG) can only be fixed by re-uploading the original, so
        say so instead of silently storing a white box. */
    const warnTransparencyLost = () =>
        (toast.warning || toast)(t('uploads.transparencyLost'), { duration: 10000 });

    const uploadLogo = async (e) => {
        const picked = e.target.files?.[0];
        if (!picked) return;
        if (!looksLikeImage(picked)) { toast.error(t('vouchers.selectImageFile')); return; }
        setUploading(true);
        beginUpload('logo', picked);
        try {
            const file = await prepareImageForUpload(picked, { maxEdge: 800, onFlattened: warnTransparencyLost });
            const res = await uploadWithProgress({
                url: `${API_BASE}/api/voucher-settings/logo`, token, file, fileField: 'logo',
                fields: { organization_id: settingsOrgId }, onProgress: trackUpload('logo'),
            });
            if (res.ok) {
                setLogoUrl(res.data.logo_url);
                toast.success(t('vouchers.logoUploaded'));
            } else { toast.error(uploadFailureMessage(res, t)); }
        } finally { endUpload('logo'); setUploading(false); }
    };

    const uploadGiftImage = async (idx, picked, slot = 'single') => {
        if (!picked) return;
        if (!looksLikeImage(picked)) { toast.error(t('vouchers.selectImageFile')); return; }
        const gift = ggGifts[idx];
        const isDouble = slot === 'double';
        const key = `gift-${idx}-${slot}`;
        beginUpload(key, picked);
        updateGift(idx, isDouble
            ? { _localDoublePreview: URL.createObjectURL(picked), _dblUploading: true }
            : { _localPreview: URL.createObjectURL(picked), _imgUploading: true });
        try {
            const file = await prepareImageForUpload(picked, { maxEdge: 900 });
            const res = await uploadWithProgress({
                url: `${API_BASE}/api/brand-assets`, token, file,
                fields: {
                    kind: isDouble ? 'gift_double' : 'gift',
                    name: `${gift.title || t('giftGenerator.giftImage')}${isDouble ? ' 2x' : ''}`,
                    organization_id: settingsOrgId,
                },
                onProgress: trackUpload(key),
            });
            if (res.ok) {
                applyGiftImage(idx, slot, res.data.url);
                toast.success(t('giftGenerator.imageUploaded'));
            } else {
                updateGift(idx, isDouble ? { _dblUploading: false } : { _imgUploading: false });
                toast.error(uploadFailureMessage(res, t));
            }
        } finally { endUpload(key); }
    };

    /** Point a gift at a library image (upload or picker). Assets are immutable,
        so the gift stores a REFERENCE — no file is copied. */
    const applyGiftImage = (idx, slot, url) => updateGift(idx, slot === 'double'
        ? { double_image_url: url, double_image_path: null, _dblUploading: false, _localDoublePreview: null }
        : { image_url: url, image_path: null, _imgUploading: false, _localPreview: null });
    const removeGiftImage = (idx) => updateGift(idx, { image_url: null, image_path: null, _localPreview: null });
    const removeGiftDoubleImage = (idx) => updateGift(idx, { double_image_url: null, double_image_path: null, _localDoublePreview: null });

    const uploadGiftWallpaper = async (picked) => {
        if (!picked) return;
        if (!looksLikeImage(picked)) { toast.error(t('vouchers.selectImageFile')); return; }
        setGgWallpaperUploading(true);
        beginUpload('wallpaper', picked);
        try {
            const file = await prepareImageForUpload(picked, { maxEdge: 1600 });
            const res = await uploadWithProgress({
                url: `${API_BASE}/api/brand-assets`, token, file,
                fields: { kind: 'wallpaper', name: picked.name || '', organization_id: settingsOrgId },
                onProgress: trackUpload('wallpaper'),
            });
            if (res.ok) {
                setGgWallpaperUrl(res.data.url);
                toast.success(t('giftGenerator.wallpaperUploaded'));
            } else { toast.error(uploadFailureMessage(res, t)); }
        } finally { endUpload('wallpaper'); setGgWallpaperUploading(false); }
    };
    const removeGiftWallpaper = () => setGgWallpaperUrl(null);

    const uploadScratchLogo = async (picked) => {
        if (!picked) return;
        if (!looksLikeImage(picked)) { toast.error(t('vouchers.selectImageFile')); return; }
        setGgScratchLogoUploading(true);
        beginUpload('scratch', picked);
        try {
            const file = await prepareImageForUpload(picked, { maxEdge: 800, onFlattened: warnTransparencyLost });
            const res = await uploadWithProgress({
                url: `${API_BASE}/api/brand-assets`, token, file,
                fields: { kind: 'scratch_logo', name: picked.name || '', organization_id: settingsOrgId },
                onProgress: trackUpload('scratch'),
            });
            if (res.ok) {
                setGgScratchLogoUrl(res.data.url);
                toast.success(t('giftGenerator.scratchLogoUploaded'));
            } else { toast.error(uploadFailureMessage(res, t)); }
        } finally { endUpload('scratch'); setGgScratchLogoUploading(false); }
    };
    const removeScratchLogo = () => setGgScratchLogoUrl(null);

    const uploadMerchantLogo = async (picked) => {
        if (!picked) return;
        if (!looksLikeImage(picked)) { toast.error(t('vouchers.selectImageFile')); return; }
        setGgMerchantLogoUploading(true);
        beginUpload('merchant', picked);
        try {
            const file = await prepareImageForUpload(picked, { maxEdge: 800, onFlattened: warnTransparencyLost });
            const res = await uploadWithProgress({
                url: `${API_BASE}/api/brand-assets`, token, file,
                fields: { kind: 'merchant_logo', name: picked.name || '', organization_id: settingsOrgId },
                onProgress: trackUpload('merchant'),
            });
            if (res.ok) {
                setGgMerchantLogoUrl(res.data.url);
                toast.success(t('giftGenerator.merchantLogoUploaded'));
            } else { toast.error(uploadFailureMessage(res, t)); }
        } finally { endUpload('merchant'); setGgMerchantLogoUploading(false); }
    };
    const removeMerchantLogo = () => setGgMerchantLogoUrl(null);

    // Brand library picker — one dialog, opened with the kind it should filter
    // to and what to do with the chosen image.
    const [libraryPicker, setLibraryPicker] = useState(null);
    const openLibrary = (kind, apply) => setLibraryPicker({ kind, apply });

    /** The org logo is persisted server-side, so picking one saves immediately. */
    const setVoucherLogoFromLibrary = async (url, asset) => {
        try {
            const res = await fetch(`${API_BASE}/api/voucher-settings/logo-from-library`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ asset_id: asset.id, organization_id: settingsOrgId }),
            });
            if (res.ok) setLogoUrl((await res.json()).logo_url);
            else toast.error(uploadFailureMessage({ status: res.status }, t));
        } catch { toast.error(t('uploads.errorNetwork')); }
    };

    // Flipping Lead Generation (or switching setup) re-collapses every
    // doubled-prize block; a manual expand only lasts until then.
    useEffect(() => { setDblExpanded({}); }, [ggLeadGenEnabled, ggSetupId]);

    // Open the ACTUAL guest Gift Generator using the current (unsaved) config —
    // reflects logo + size, wallpaper, gifts/images, wording, reveal method and
    // exchange. No save, no real voucher minted.
    const openGiftPreview = () => {
        const abs = (u) => (u ? (String(u).startsWith('http') || String(u).startsWith('blob:') ? u : `${API_BASE}${u}`) : null);
        const cfg = {
            enabled: true,
            reveal_method: ggRevealMethod === 'scratch' ? 'scratch' : 'button',
            lead_generation_enabled: !!ggLeadGenEnabled,
            lead_generation_url: (ggLeadGenUrl || '').trim(),
            gift_generator_mode: 'game',
            exchange_enabled: !!ggExchangeEnabled,
            exchange_limit: Number(ggExchangeLimit) || 0,
            logo_size_px: ggLogoSizePx,
            merchant_logo_url: abs(logoUrl),
            card_style: cardStyle,
            organization_name: '',
            wallpaper_url: abs(ggWallpaperUrl),
            merchant_logo_url: abs(ggMerchantLogoUrl) || abs(logoUrl),
            scratch_surface_logo_url: ggRevealMethod === 'scratch' ? abs(ggScratchLogoUrl) : null,
            scratch_surface_logo_opacity: Number(ggScratchLogoOpacity) || 30,
            scratch_surface_logo_size: Number(ggScratchLogoSize) || 38,
            copy: ggCopy || {},
            gifts: (ggGifts || []).map(g => ({
                id: g.id,
                title: g.title,
                description: g.description,
                value: Number(g.value) || 0,
                // drives the "$X doubled" vs "2x <item>" wording in the preview
                is_monetary: g.gift_type === 'monetary',
                image_url: abs(g.image_url || g._localPreview),
                double_image_url: abs(g.double_image_url || g._localDoublePreview),
                remaining: g.remaining,
            })),
        };
        try { localStorage.setItem('ggPreviewConfig', JSON.stringify(cfg)); } catch (_) { /* ignore */ }
        window.open('/demo/preview?preview=1', '_blank', 'noopener');
    };

    const addAmount = () => {
        const val = parseFloat(newAmount);
        if (!val || val <= 0 || val > 500) return;
        if (presetAmounts.includes(val)) return;
        setPresetAmounts(prev => [...prev, val].sort((a, b) => a - b));
        setNewAmount('');
    };
    const removeAmount = (val) => setPresetAmounts(prev => prev.filter(a => a !== val));

    const addExpiry = () => {
        const val = parseInt(newExpiry);
        if (!val || val <= 0 || val > 365) return;
        if (presetExpiry.includes(val)) return;
        setPresetExpiry(prev => [...prev, val].sort((a, b) => a - b));
        setNewExpiry('');
    };
    const removeExpiry = (val) => setPresetExpiry(prev => prev.filter(e => e !== val));

    // ── Gift Generator helpers ──
    const addGift = () => {
        if (ggGifts.length >= 20) { toast.error(t('giftGenerator.maxReached')); return; }
        const id = (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : `gift-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setGgGifts(prev => [...prev, { id, title: '', description: '', gift_type: 'non_monetary', value: 0, quantity: 1, remaining: undefined }]);
    };
    const updateGift = (idx, patch) => setGgGifts(prev => prev.map((g, i) => i === idx ? { ...g, ...patch } : g));
    const removeGift = (idx) => setGgGifts(prev => prev.filter((_, i) => i !== idx));

    // Preview data
    const previewCode = 'RV-A7K3M9Q';
    const activePreviewAmount = previewAmount || presetAmounts[0] || 25;
    const activePreviewExpiry = previewExpiry || defaultExpiry || 30;
    const previewExpiryDate = new Date(Date.now() + activePreviewExpiry * 86400000).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
    });
    const previewReasonObj = VOUCHER_REASONS.find(r => r.code === previewReason) || VOUCHER_REASONS[6];
    const previewStripe = STRIPE_PREVIEW_CLASSES[previewReasonObj.stripe] || STRIPE_PREVIEW_CLASSES.green;

    // ── Voucher Design derived values ────────────────────────────────
    // Effective colors for the live preview: use the org override when
    // set, otherwise fall back to today's default appearance.
    const designHasBg = !!voucherBg;
    const designHasText = !!voucherText;
    const designHasAccent = !!voucherAccent;
    const effAccentColor = voucherAccent || null;         // null → keep reason stripe class
    const effTextColor = voucherText || '#E2E8F0';
    const previewBgStyle = designHasBg
        ? { background: `linear-gradient(180deg, ${_lighten(voucherBg, 0.08)} 0%, ${voucherBg} 100%)` }
        : null;
    // Contrast guard — warn when text on background is below WCAG AA (4.5).
    const designContrast = (designHasBg || designHasText)
        ? contrastRatio(voucherBg || VOUCHER_DESIGN_DEFAULTS.bg, voucherText || VOUCHER_DESIGN_DEFAULTS.text)
        : null;
    const designLowContrast = designContrast !== null && designContrast < 4.5;
    const activeDesignPreset = VOUCHER_DESIGN_PRESETS.find(
        (p) => (p.bg || '') === (voucherBg || '') && (p.text || '') === (voucherText || '') && (p.accent || '') === (voucherAccent || '')
    );
    const applyDesignPreset = (p) => {
        setVoucherBg(p.bg || '');
        setVoucherText(p.text || '');
        setVoucherAccent(p.accent || '');
    };

    // Allocation helpers
    const selectedRecipient = recipients.find(r => r.id === allocRecipient);
    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const addToAllocation = () => {
        const effectiveAmount = allocAmount === 'custom' ? parseFloat(allocCustomAmount) : parseFloat(allocAmount);
        if (!effectiveAmount || effectiveAmount <= 0 || allocQty < 1) return;
        if (!isElevated && effectiveAmount > maxVoucherValue) {
            toast.error(t('vouchers.maxValueExceeded', { max: maxVoucherValue })); return;
        }
        const baseItem = { value: effectiveAmount, quantity: allocQty, available_for_loyal_review: allocAvailableForLoyalReview };
        if (allocDeliveryMethod === 'staff_card') {
            if (!allocRecipient) return;
            if (allocRecipient === '__general__') {
                setAllocList(prev => [...prev, {
                    _key: `alloc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                    recipient_id: null, recipient_name: 'General Use', recipient_type: 'general_use',
                    delivery_method: 'staff_card', ...baseItem,
                }]);
                setAllocRecipient('');
            } else {
                const rec = selectedRecipient;
                if (!rec) return;
                setAllocList(prev => [...prev, {
                    _key: `alloc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                    recipient_id: rec.id, recipient_name: rec.name, recipient_type: rec.type,
                    delivery_method: 'staff_card', ...baseItem,
                }]);
                setAllocRecipient('');
            }
        } else if (allocDeliveryMethod === 'email') {
            if (!allocEmail.trim() || !isValidEmail(allocEmail.trim())) { toast.error(t('vouchers.invalidEmail')); return; }
            setAllocList(prev => [...prev, {
                _key: `alloc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                recipient_id: `email:${allocEmail.trim()}`, recipient_name: allocEmail.trim(),
                recipient_type: 'email', delivery_method: 'email', email: allocEmail.trim(), ...baseItem,
            }]);
            setAllocEmail('');
        } else if (allocDeliveryMethod === 'sms') {
            if (!allocPhone.trim() || !allocPhone.trim().startsWith('+')) { toast.error(t('vouchers.invalidPhoneFormat')); return; }
            setAllocList(prev => [...prev, {
                _key: `alloc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                recipient_id: `sms:${allocPhone.trim()}`, recipient_name: allocPhone.trim(),
                recipient_type: 'email', delivery_method: 'sms', phone: allocPhone.trim(), ...baseItem,
            }]);
            setAllocPhone('');
        } else if (allocDeliveryMethod === 'email_and_sms') {
            if (!allocEmail.trim() || !isValidEmail(allocEmail.trim())) { toast.error(t('vouchers.invalidEmail')); return; }
            if (!allocPhone.trim() || !allocPhone.trim().startsWith('+')) { toast.error(t('vouchers.invalidPhoneFormat')); return; }
            setAllocList(prev => [...prev, {
                _key: `alloc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                recipient_id: `dual:${allocEmail.trim()}`, recipient_name: `${allocEmail.trim()} / ${allocPhone.trim()}`,
                recipient_type: 'email', delivery_method: 'email_and_sms',
                email: allocEmail.trim(), phone: allocPhone.trim(), ...baseItem,
            }]);
            setAllocEmail(''); setAllocPhone('');
        }
        setAllocAmount(''); setAllocCustomAmount(''); setAllocQty(1);
        setAllocAvailableForLoyalReview(false);
    };

    const removeFromAllocation = (idx) => setAllocList(prev => prev.filter((_, i) => i !== idx));
    const allocTotalVouchers = allocList.reduce((s, i) => s + i.quantity, 0);
    const allocTotalValue = allocList.reduce((s, i) => s + i.value * i.quantity, 0);

    const methodLabels = {
        'staff_card': t('vouchers.staffCard'),
        'email': t('vouchers.email'),
        'sms': t('vouchers.sms'),
        'email_and_sms': t('vouchers.emailAndSms'),
    };
    const methodColors = {
        'staff_card': 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
        'email': 'bg-blue-500/10 text-blue-600 border-blue-500/30',
        'sms': 'bg-amber-500/10 text-amber-600 border-amber-500/30',
        'email_and_sms': 'bg-violet-500/10 text-violet-600 border-violet-500/30',
    };

    const submitAllocation = async () => {
        setAllocating(true);
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/allocate`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    location_id: allocLocation,
                    reason_code: allocReason,
                    items: allocList.map(item => ({
                        recipient_id: item.recipient_id,
                        recipient_name: item.recipient_name,
                        recipient_type: item.recipient_type,
                        delivery_method: item.delivery_method || 'staff_card',
                        email: item.email || null,
                        phone: item.phone || null,
                        value: item.value,
                        quantity: item.quantity,
                        available_for_loyal_review: !!item.available_for_loyal_review,
                    })),
                }),
            });
            if (res.ok) {
                const data = await res.json();
                let msg = t('vouchers.vouchersAllocated', { count: data.total_vouchers, value: data.total_value });
                if (data.email_sent > 0) msg += ` — ${t('vouchers.emailSent', { count: data.email_sent })}`;
                if (data.sms_sent > 0) msg += ` — ${t('vouchers.smsSent', { count: data.sms_sent })}`;
                toast.success(msg);
                setAllocList([]); setAllocConfirmOpen(false);
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(stringifyDetail(err.detail) || t('vouchers.allocationFailed'));
            }
        } catch { toast.error(t('vouchers.connectionError')); }
        finally { setAllocating(false); }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
        );
    }

    // Toggle location expand
    const toggleLoc = (id) => setExpandedLocs(prev => ({ ...prev, [id]: !prev[id] }));

    const staffByLoc = (locId) => (stats?.staff || []).filter(s => (s.location_id || 'unassigned') === locId);

    return (
        <div className="space-y-6" data-testid="voucher-settings-page">
            {/* Header — dropped when embedded (the host surface owns the title) */}
            {!embedded && (
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Gift className="w-6 h-6 text-amber-500" />
                        {t('vouchers.settingsTitle')}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {t('vouchers.manageVouchersSubtitle')}
                    </p>
                </div>
            </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="voucher-tabs">
                <TabsList className="max-w-full overflow-x-auto justify-start">
                    <TabsTrigger value="settings" data-testid="tab-settings">
                        <Settings className="w-4 h-4 mr-1.5" />
                        {t('vouchers.tabSettings')}
                    </TabsTrigger>
                    {canManageIncentives && (
                        <TabsTrigger value="incentives" data-testid="tab-incentives">
                            <Wallet className="w-4 h-4 mr-1.5" />
                            {t('vouchers.tabIncentives')}
                        </TabsTrigger>
                    )}
                    {canViewStatistics && (
                        <TabsTrigger value="statistics" data-testid="tab-statistics">
                            <BarChart3 className="w-4 h-4 mr-1.5" />
                            {t('vouchers.tabStatistics')}
                        </TabsTrigger>
                    )}
                    {canViewStatistics && (
                        <TabsTrigger value="audit_reports" data-testid="tab-audit-reports">
                            <CalendarClock className="w-4 h-4 mr-1.5" />
                            {t('voucher_audit.reports.tab_label')}
                        </TabsTrigger>
                    )}
                    <TabsTrigger value="recipients" data-testid="tab-recipients">
                        <Users className="w-4 h-4 mr-1.5" />
                        {t('vouchers.tabRecipients', 'Recipients')}
                    </TabsTrigger>
                </TabsList>

                {/* ── TAB 1: VOUCHER SETTINGS ── */}
                <TabsContent value="settings" className="space-y-6 mt-6">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        {isSuperAdmin && organizations.length > 1 ? (
                            <div className="space-y-1 max-w-sm min-w-[240px]">
                                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Building2 className="w-3 h-3" /> {t('vouchers.organization')}
                                </Label>
                                <Select value={settingsOrgId} onValueChange={setSettingsOrgId} disabled={!!scopeOrgId}>
                                    <SelectTrigger data-testid="settings-org-select">
                                        <SelectValue placeholder={t('vouchers.selectOrganization')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {organizations.map(org => (
                                            <SelectItem key={org.id} value={org.id}>
                                                {org.name || org.display_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        ) : <div />}
                        <Button
                            onClick={saveSettings}
                            disabled={saving || !settingsOrgId || !spikePhonesOk}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="save-voucher-settings-btn"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                            {t('vouchers.saveSettings')}
                        </Button>
                    </div>

                    {/* ── Loyal Review download-abuse alerts (org-scoped) ── */}
                    <Card data-testid="voucher-spike-settings">
                        <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2">
                                <ShieldAlert className="w-4 h-4 text-amber-500" />
                                {t('voucherSpike.sectionTitle', 'Download abuse alerts')}
                            </CardTitle>
                            <p className="text-sm text-muted-foreground">
                                {t('voucherSpike.sectionHint', 'We alert you once a day if voucher downloads spike above your threshold.')}
                            </p>
                        </CardHeader>
                        <CardContent className="space-y-5">
                            <div className="space-y-1.5 max-w-xs">
                                <Label className="text-xs text-muted-foreground">
                                    {t('voucherSpike.thresholdLabel', 'Alert me after this many downloads in one day')}
                                </Label>
                                <Input type="number" min={0} max={10000} value={spikeThreshold}
                                    onChange={(e) => setSpikeThreshold(e.target.value)}
                                    data-testid="voucher-spike-threshold" />
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">
                                    {t('voucherSpike.emailsLabel', 'Alert email addresses')}
                                </Label>
                                <div className="flex gap-2">
                                    <Input value={spikeEmailDraft} type="email"
                                        placeholder={t('voucherSpike.emailsPlaceholder', 'manager@example.com')}
                                        onChange={(e) => setSpikeEmailDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key !== 'Enter') return;
                                            e.preventDefault();
                                            const v = spikeEmailDraft.trim().toLowerCase();
                                            if (v && v.includes('@') && !spikeEmails.includes(v)) {
                                                setSpikeEmails([...spikeEmails, v]);
                                                setSpikeEmailDraft('');
                                            }
                                        }}
                                        data-testid="voucher-spike-email-input" />
                                    <Button type="button" variant="outline" data-testid="voucher-spike-email-add"
                                        onClick={() => {
                                            const v = spikeEmailDraft.trim().toLowerCase();
                                            if (v && v.includes('@') && !spikeEmails.includes(v)) {
                                                setSpikeEmails([...spikeEmails, v]);
                                                setSpikeEmailDraft('');
                                            }
                                        }}>
                                        {t('voucherSpike.addEmail', 'Add email')}
                                    </Button>
                                </div>
                                {spikeEmails.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5" data-testid="voucher-spike-email-badges">
                                        {spikeEmails.map((addr) => (
                                            <Badge key={addr} variant="secondary" className="gap-1.5"
                                                data-testid={`voucher-spike-email-badge-${addr}`}>
                                                {addr}
                                                <button type="button" className="hover:text-red-500"
                                                    onClick={() => setSpikeEmails(spikeEmails.filter((a) => a !== addr))}
                                                    data-testid={`voucher-spike-email-remove-${addr}`}>×</button>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">
                                    {t('voucherSpike.phonesLabel', 'Alert text messages')}
                                </Label>
                                <RecipientListEditor value={spikePhones} onChange={setSpikePhones}
                                    onValidityChange={setSpikePhonesOk} testid="voucher-spike-phones" />
                            </div>

                            {spikeEmails.length === 0 && spikePhones.length === 0 && (
                                <p className="text-xs text-amber-500" data-testid="voucher-spike-not-configured">
                                    {t('voucherSpike.notConfigured', 'No recipients yet — nobody will be alerted.')}
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Left column — Configuration */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Gift Generator */}
                            <Card data-insight-id="vouchers.setting.gift-generator" data-testid="gift-generator-card">
                                <CardHeader className="pb-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <Gift className="w-4 h-4 text-fuchsia-500" />
                                            {t('giftGenerator.settingsTitle')}
                                        </CardTitle>
                                        <Switch
                                            checked={ggEnabled}
                                            onCheckedChange={setGgEnabled}
                                            data-testid="gg-enable-switch"
                                        />
                                    </div>
                                    <p className="text-xs text-muted-foreground">{t('giftGenerator.subtitle')}</p>
                                    {/* Game Results — every scratch this client has had, in one place. */}
                                    <a href={`/game-results?organization_id=${settingsOrgId || ''}`}
                                       className="inline-flex items-center gap-2 text-xs font-medium text-blue-600 hover:underline"
                                       data-testid="gg-game-results-link">
                                        <Gamepad2 className="w-3.5 h-3.5" />
                                        {t('gameResults.title', 'Game Results')}
                                    </a>
                                    <a href={`/live-game-view?organization_id=${settingsOrgId || ''}`}
                                       className="inline-flex items-center gap-2 text-xs font-medium text-emerald-600 hover:underline"
                                       data-testid="gg-live-game-link">
                                        <Radio className="w-3.5 h-3.5" />
                                        {t('liveGame.title', 'Live Game View')}
                                    </a>
                                    <div className="pt-3">
                                        <GiftSetupPicker
                                            setups={ggSetups}
                                            valueId={ggSetupId}
                                            busy={ggSetupBusy}
                                            onSelect={selectGgSetup}
                                            onCreate={createGgSetup}
                                            onDuplicate={duplicateGgSetup}
                                            onRename={renameGgSetup}
                                            onSetDefault={setGgSetupDefault}
                                            onDelete={deleteGgSetup}
                                        />
                                    </div>
                                    {/* The Gift Generator is off, but this venue runs the
                                        Tap’N Win: its saved gifts can still be won there. */}
                                    {!ggEnabled && goalLink.available && (
                                        <p className="pt-2 text-xs text-muted-foreground"
                                           data-testid="gg-goal-still-usable">
                                            {t('goalGame.giftGeneratorOffNote')}{' '}
                                            <a href={`/goal-game?organization_id=${settingsOrgId || ''}`}
                                               className="font-semibold underline"
                                               data-testid="gg-goal-prizes-link">
                                                {t('goalGame.giftGeneratorOffLink')}
                                            </a>
                                        </p>
                                    )}
                                </CardHeader>
                                {/* The gifts and their own Save buttons are reachable
                                    whether or not the master switch is on: a venue that
                                    runs Tap’N Win saves gift cards from here too. */}
                                {(ggEnabled || goalLink.available) && (
                                    <CardContent className="space-y-4" data-testid="gg-config-body">
                                        {/* Full-flow guest preview (uses current unsaved config) */}
                                        <Button
                                            type="button"
                                            onClick={openGiftPreview}
                                            data-testid="gg-preview-btn"
                                            className="w-full h-12 bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:brightness-110 text-white font-bold gap-2"
                                        >
                                            <Eye className="w-4 h-4" />
                                            {t('giftGenerator.previewButton')}
                                        </Button>

                                        {/* Experience mode — Game vs Review */}
                                        <div className="rounded-lg border border-border bg-muted/30 px-3 py-3 space-y-2" data-testid="gg-mode">
                                            <Label className="text-sm font-medium">{t('giftGenerator.modeTitle')}</Label>
                                            {[
                                                { id: 'game', label: t('giftGenerator.modeGame'), desc: t('giftGenerator.modeGameDesc') },
                                                { id: 'review', label: t('giftGenerator.modeReview'), desc: t('giftGenerator.modeReviewDesc') },
                                            ].map(opt => (
                                                <button
                                                    key={opt.id}
                                                    type="button"
                                                    onClick={() => setGgMode(opt.id)}
                                                    data-testid={`gg-mode-${opt.id}`}
                                                    className={'w-full text-left flex items-start gap-3 rounded-lg border-2 px-3 py-2 transition-colors ' + (ggMode === opt.id ? 'border-fuchsia-500 bg-fuchsia-500/5' : 'border-border hover:border-fuchsia-300')}
                                                >
                                                    <span className={'mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 ' + (ggMode === opt.id ? 'border-fuchsia-500 bg-fuchsia-500' : 'border-muted-foreground')} />
                                                    <span>
                                                        <span className="block text-sm font-semibold">{opt.label}</span>
                                                        <span className="block text-xs text-muted-foreground">{opt.desc}</span>
                                                    </span>
                                                </button>
                                            ))}
                                        </div>

                                        {/* Customer-facing text (editable, per language) */}
                                        <div className="rounded-lg border border-border bg-muted/30 px-3 py-3 space-y-3" data-testid="gg-copy-editor">
                                            <div>
                                                <Label className="text-sm font-medium">{t('giftGenerator.copyTitle')}</Label>
                                                <p className="text-xs text-muted-foreground mt-0.5">{t('giftGenerator.copyHint')}</p>
                                            </div>
                                            <div className="flex gap-1.5">
                                                {['en', 'fr', 'es'].map(lng => (
                                                    <button
                                                        key={lng}
                                                        type="button"
                                                        onClick={() => setGgCopyLang(lng)}
                                                        data-testid={`gg-copy-lang-${lng}`}
                                                        className={'px-3 py-1 rounded-full text-xs font-bold uppercase transition-colors ' + (ggCopyLang === lng ? 'bg-fuchsia-500 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/70')}
                                                    >
                                                        {lng}
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="space-y-2">
                                                {[
                                                    { key: 'headlineLine1', label: t('giftGenerator.copyFieldHeadline'), def: 'heroUnlockedPre' },
                                                    { key: 'headlineHighlight', label: t('giftGenerator.copyFieldHighlight'), def: 'heroUnlockedHighlight' },
                                                    { key: 'tapForGift', label: t('giftGenerator.copyFieldTapForGift'), def: 'tapForGift' },
                                                    { key: 'generateButton', label: t('giftGenerator.copyFieldGenerateButton'), def: 'cta' },
                                                    { key: 'generatingTitle', label: t('giftGenerator.copyFieldGeneratingTitle'), def: 'generating' },
                                                    { key: 'generatingSub', label: t('giftGenerator.copyFieldGeneratingSub'), def: 'generatingSub' },
                                                    { key: 'revealTitle', label: t('giftGenerator.copyFieldRevealTitle'), def: 'revealTitle' },
                                                    { key: 'scratchInstruction', label: t('giftGenerator.copyFieldScratchInstruction'), def: 'scratchInstruction' },
                                                    { key: 'acceptButton', label: t('giftGenerator.copyFieldAcceptButton'), def: 'accept' },
                                                    { key: 'exchangeButton', label: t('giftGenerator.copyFieldExchangeButton'), def: 'exchange' },
                                                    { key: 'keepPrompt', label: t('giftGenerator.copyFieldKeepPrompt'), def: 'keepPrompt' },
                                                ].map(f => (
                                                    <div key={f.key} className="space-y-1">
                                                        <Label className="text-[11px] text-muted-foreground">{f.label}</Label>
                                                        <Input
                                                            value={ggCopy?.[ggCopyLang]?.[f.key] || ''}
                                                            placeholder={i18n.getFixedT(ggCopyLang, null)(`giftGenerator.${f.def}`)}
                                                            onChange={(e) => setGgCopy(prev => ({
                                                                ...prev,
                                                                [ggCopyLang]: { ...(prev?.[ggCopyLang] || {}), [f.key]: e.target.value },
                                                            }))}
                                                            className="h-8 text-xs"
                                                            data-testid={`gg-copy-${f.key}-input`}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Exchange controls */}
                                        <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-muted/30 px-3 py-3">
                                            <div className="flex items-center gap-2">
                                                <Switch
                                                    checked={ggExchangeEnabled}
                                                    onCheckedChange={setGgExchangeEnabled}
                                                    data-testid="gg-exchange-switch"
                                                />
                                                <Label className="text-sm">{t('giftGenerator.exchangeEnable')}</Label>
                                            </div>
                                            {ggExchangeEnabled && (
                                                <div className="flex items-center gap-2">
                                                    <Label className="text-xs text-muted-foreground">{t('giftGenerator.exchangeLimitLabel')}</Label>
                                                    <Input
                                                        type="number"
                                                        min={0}
                                                        max={10}
                                                        value={ggExchangeLimit}
                                                        onChange={(e) => setGgExchangeLimit(Math.max(0, Math.min(10, parseInt(e.target.value) || 0)))}
                                                        className="w-20"
                                                        data-testid="gg-exchange-limit-input"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        {ggExchangeEnabled && (
                                            <p className="text-xs text-muted-foreground -mt-2">{t('giftGenerator.exchangeLimitHelp')}</p>
                                        )}

                                        {/* Reveal method */}
                                        <div className="rounded-lg border border-border bg-muted/30 px-3 py-3 space-y-2" data-testid="gg-reveal-method">
                                            <Label className="text-sm font-medium">{t('giftGenerator.revealMethodTitle')}</Label>
                                            {[
                                                { id: 'button', label: t('giftGenerator.revealButton'), desc: t('giftGenerator.revealButtonDesc') },
                                                { id: 'scratch', label: t('giftGenerator.revealScratch'), desc: t('giftGenerator.revealScratchDesc') },
                                            ].map(opt => (
                                                <button
                                                    key={opt.id}
                                                    type="button"
                                                    onClick={() => setGgRevealMethod(opt.id)}
                                                    data-testid={`gg-reveal-${opt.id}-btn`}
                                                    className={'w-full text-left flex items-start gap-3 rounded-lg border-2 px-3 py-2 transition-colors ' + (ggRevealMethod === opt.id ? 'border-fuchsia-500 bg-fuchsia-500/5' : 'border-border hover:border-fuchsia-300')}
                                                >
                                                    <span className={'mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 ' + (ggRevealMethod === opt.id ? 'border-fuchsia-500 bg-fuchsia-500' : 'border-muted-foreground')} />
                                                    <span>
                                                        <span className="block text-sm font-semibold">{opt.label}</span>
                                                        <span className="block text-xs text-muted-foreground">{opt.desc}</span>
                                                    </span>
                                                </button>
                                            ))}
                                        </div>

                                        {/* Scratch surface logo — only meaningful for the
                                            scratch reveal, so it stays hidden otherwise. */}
                                        {ggRevealMethod === 'scratch' && (
                                            <div className="rounded-lg border border-border bg-muted/30 px-3 py-3 space-y-3" data-testid="gg-scratch-logo-card">
                                                <div>
                                                    <Label className="text-sm font-medium">{t('giftGenerator.scratchLogoTitle')}</Label>
                                                    <p className="text-xs text-muted-foreground mt-0.5">{t('giftGenerator.scratchLogoHint')}</p>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className="relative w-24 h-24 rounded-lg border border-border overflow-hidden shrink-0"
                                                        style={{ background: 'linear-gradient(135deg,#eef0f5 0%,#c9cdd7 25%,#9aa0ac 50%,#c9cdd7 75%,#8b909c 100%)' }}
                                                        data-testid="gg-scratch-logo-preview"
                                                    >
                                                        {/* Brushed-metal streaks — the foil affordance the
                                                            watermark must never bury. */}
                                                        <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.22) 0 1px, transparent 1px 7px)' }} />
                                                        {(ggScratchLogoUrl || uploadState.scratch) && (
                                                            <img
                                                                src={assetSrc('scratch', ggScratchLogoUrl)}
                                                                alt=""
                                                                data-testid="gg-scratch-logo-preview-img"
                                                                className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 object-contain"
                                                                style={{ top: '42%', maxWidth: `${Math.min(100, ggScratchLogoSize * 1.3)}%`, maxHeight: `${ggScratchLogoSize}%`, opacity: ggScratchLogoOpacity / 100 }}
                                                            />
                                                        )}
                                                        <span
                                                            className={'absolute left-0 right-0 text-center text-[8px] font-bold tracking-wider text-slate-800/60 uppercase ' + (ggScratchLogoUrl ? 'bottom-1.5' : 'top-1/2 -translate-y-1/2')}
                                                        >
                                                            {t('giftGenerator.scratchHint', 'Scratch here')}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col gap-1.5">
                                                        <span className="text-[11px] text-muted-foreground">
                                                            {ggScratchLogoUrl ? t('giftGenerator.scratchLogoTitle') : t('giftGenerator.scratchLogoDefault')}
                                                        </span>
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                id="gg-scratch-logo-input"
                                                                type="file"
                                                                accept="image/*"
                                                                className="hidden"
                                                                onChange={(e) => uploadScratchLogo(e.target.files?.[0])}
                                                                data-testid="gg-scratch-logo-input"
                                                            />
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => document.getElementById('gg-scratch-logo-input')?.click()}
                                                                disabled={ggScratchLogoUploading}
                                                                data-testid="gg-scratch-logo-upload-btn"
                                                            >
                                                                {ggScratchLogoUploading
                                                                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                                    : <Upload className="w-3 h-3 mr-1" />}
                                                                {ggScratchLogoUrl ? t('giftGenerator.replaceImage') : t('giftGenerator.uploadScratchLogo')}
                                                            </Button>
                                                            {ggScratchLogoUrl && (
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={removeScratchLogo}
                                                                    data-testid="gg-scratch-logo-remove-btn"
                                                                    className="text-muted-foreground hover:text-red-500"
                                                                >
                                                                    {t('giftGenerator.removeImage')}
                                                                </Button>
                                                            )}
                                                            <Button
                                                                type="button" variant="ghost" size="sm"
                                                                onClick={() => openLibrary('scratch_logo', (url) => setGgScratchLogoUrl(url))}
                                                                data-testid="gg-scratch-logo-library-btn"
                                                            >
                                                                <ImageIcon className="w-3 h-3 mr-1" /> {t('brandLibrary.openPicker')}
                                                            </Button>
                                                            <RemoveBackgroundButton
                                                                url={ggScratchLogoUrl} kind="scratch_logo"
                                                                onApply={(u) => setGgScratchLogoUrl(u)}
                                                                testId="gg-scratch-logo-removebg-btn" />
                                                        </div>
                                                        <UploadProgress state={uploadState.scratch} testId="gg-scratch-logo-progress" />
                                                        {/* This upload already lands in the Brand library. */}
                                                        <p className="text-[11px] text-muted-foreground" data-testid={'gg-scratch-logo-library-note'}>
                                                            {t('brandLibrary.savedNote')}
                                                        </p>
                                                    </div>
                                                </div>
                                                {ggScratchLogoUrl && (
                                                    <div className="pt-1 space-y-1.5" data-testid="gg-scratch-logo-opacity-row">
                                                        <div className="flex items-center justify-between">
                                                            <Label className="text-xs font-medium">{t('giftGenerator.scratchLogoOpacity')}</Label>
                                                            <span className="text-xs font-semibold tabular-nums" data-testid="gg-scratch-logo-opacity-value">
                                                                {ggScratchLogoOpacity}%
                                                            </span>
                                                        </div>
                                                        <input
                                                            type="range"
                                                            min={15}
                                                            max={60}
                                                            step={1}
                                                            value={ggScratchLogoOpacity}
                                                            onChange={(e) => setGgScratchLogoOpacity(Number(e.target.value))}
                                                            data-testid="gg-scratch-logo-opacity-slider"
                                                            className="w-full accent-fuchsia-500 cursor-pointer"
                                                        />
                                                        <p className="text-[11px] text-muted-foreground">{t('giftGenerator.scratchLogoOpacityHint')}</p>
                                                        <div className="flex items-center justify-between pt-1.5">
                                                            <Label className="text-xs font-medium">{t('giftGenerator.scratchLogoSize')}</Label>
                                                            <span className="text-xs font-semibold tabular-nums" data-testid="gg-scratch-logo-size-value">
                                                                {ggScratchLogoSize}%
                                                            </span>
                                                        </div>
                                                        <input
                                                            type="range"
                                                            min={25}
                                                            max={100}
                                                            step={1}
                                                            value={ggScratchLogoSize}
                                                            onChange={(e) => setGgScratchLogoSize(Number(e.target.value))}
                                                            data-testid="gg-scratch-logo-size-slider"
                                                            className="w-full accent-fuchsia-500 cursor-pointer"
                                                        />
                                                        <p className="text-[11px] text-muted-foreground">{t('giftGenerator.scratchLogoSizeHint')}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* LEAD GENERATION — optional extra step between the
                                            reveal and the voucher. Sits beside reveal_method
                                            because it is the same kind of campaign setting. */}
                                        <div className="rounded-lg border border-border bg-muted/30 px-3 py-3 space-y-2" data-testid="gg-lead-generation">
                                            <div className="flex items-start gap-3">
                                                <Switch checked={ggLeadGenEnabled} onCheckedChange={setGgLeadGenEnabled}
                                                    data-testid="gg-lead-generation-toggle" className="mt-0.5" />
                                                <div className="flex-1 min-w-0">
                                                    <Label className="text-sm font-medium">{t('leadGeneration.settingsTitle', 'Lead Generation')}</Label>
                                                    <p className="text-xs text-muted-foreground mt-0.5">
                                                        {t('leadGeneration.settingsHint', 'Offer twice the prize in exchange for an app download. Off by default — when off, the gift flow works exactly as it does today.')}
                                                    </p>
                                                </div>
                                            </div>
                                            {ggLeadGenEnabled && (
                                                <Input value={ggLeadGenUrl} onChange={(e) => setGgLeadGenUrl(e.target.value)}
                                                    placeholder="https://apps.apple.com/…"
                                                    data-testid="gg-lead-generation-url"
                                                    aria-label={t('leadGeneration.settingsUrl', 'Where “Continue” sends them (https://…)')} />
                                            )}
                                        </div>

                                        {/* Card / Voucher Style — exposes the EXISTING org-level
                                            card_style (frosted/clear/solid) consumed by the Gift
                                            Generator reward card via the shared cardSurfaceClass. */}
                                        <div className="rounded-lg border border-border bg-muted/30 px-3 py-3 space-y-2" data-testid="gg-card-style">
                                            <Label className="text-sm font-medium">{t('giftGenerator.cardStyleTitle', 'Card / Voucher Style')}</Label>
                                            <p className="text-xs text-muted-foreground -mt-1">{t('giftGenerator.cardStyleHint', 'How the gift card surface sits over the guest wallpaper.')}</p>
                                            <div className="flex flex-wrap gap-2">
                                                {[
                                                    { id: 'frosted', label: t('giftGenerator.cardStyleFrosted', 'Frosted'), desc: t('giftGenerator.cardStyleFrostedDesc', 'Translucent, blurred wallpaper') },
                                                    { id: 'clear', label: t('giftGenerator.cardStyleClear', 'Clear'), desc: t('giftGenerator.cardStyleClearDesc', 'Transparent, wallpaper shows through') },
                                                    { id: 'solid', label: t('giftGenerator.cardStyleSolid', 'Solid'), desc: t('giftGenerator.cardStyleSolidDesc', 'Opaque card') },
                                                ].map(opt => (
                                                    <button
                                                        key={opt.id}
                                                        type="button"
                                                        onClick={() => setCardStyle(opt.id)}
                                                        data-testid={`gg-card-style-${opt.id}-btn`}
                                                        title={opt.desc}
                                                        className={'flex-1 min-w-[92px] text-left rounded-lg border-2 px-3 py-2 transition-colors ' + (cardStyle === opt.id ? 'border-fuchsia-500 bg-fuchsia-500/5' : 'border-border hover:border-fuchsia-300')}
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            <span className={'w-3.5 h-3.5 rounded-full border-2 shrink-0 ' + (cardStyle === opt.id ? 'border-fuchsia-500 bg-fuchsia-500' : 'border-muted-foreground')} />
                                                            <span className="text-sm font-semibold">{opt.label}</span>
                                                        </span>
                                                        <span className="block text-[11px] text-muted-foreground mt-0.5">{opt.desc}</span>
                                                    </button>
                                                ))}
                                            </div>
                                            {/* Live surface swatch — reuses the SAME cardSurfaceClass the guest game uses */}
                                            <div
                                                className="relative mt-1 h-20 rounded-lg overflow-hidden border border-border"
                                                style={{ backgroundImage: `url(${ggWallpaperUrl ? `${API_BASE}${ggWallpaperUrl}` : '/branding/gg-default-wallpaper.jpg'})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                                                data-testid="gg-card-style-swatch"
                                            >
                                                <div className={`absolute inset-3 rounded-lg flex items-center justify-center ${cardSurfaceClass(cardStyle, 'dark')}`}>
                                                    <span className="text-[11px] font-semibold text-white/90 uppercase tracking-wide">{t(`giftGenerator.cardStyle${cardStyle.charAt(0).toUpperCase() + cardStyle.slice(1)}`, cardStyle)}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Game logo size */}
                                        <div className="rounded-lg border border-border bg-muted/30 px-3 py-3 space-y-3" data-testid="gg-logo-size-card">
                                            <div>
                                                <Label className="text-sm font-medium">{t('giftGenerator.logoSizeTitle', 'Game Logo Size')}</Label>
                                                <p className="text-xs text-muted-foreground mt-0.5">{t('giftGenerator.logoSizeHint', 'Controls how large your logo appears at the top of the guest game.')}</p>
                                            </div>
                                            {/* Upload/Replace/Remove sits WITH the size slider: the
                                                merchant logo is a per-setup override, so a sponsor
                                                package can show the sponsor's mark. Empty falls back
                                                to the organization/voucher logo. */}
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <input
                                                    id="gg-merchant-logo-input"
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={(e) => uploadMerchantLogo(e.target.files?.[0])}
                                                    data-testid="gg-merchant-logo-input"
                                                />
                                                <Button
                                                    type="button" variant="outline" size="sm"
                                                    onClick={() => document.getElementById('gg-merchant-logo-input')?.click()}
                                                    disabled={ggMerchantLogoUploading}
                                                    data-testid="gg-merchant-logo-upload-btn"
                                                >
                                                    {ggMerchantLogoUploading
                                                        ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                        : <Upload className="w-3 h-3 mr-1" />}
                                                    {ggMerchantLogoUrl
                                                        ? t('giftGenerator.replaceImage')
                                                        : t('giftGenerator.uploadMerchantLogo')}
                                                </Button>
                                                {ggMerchantLogoUrl && (
                                                    <Button
                                                        type="button" variant="ghost" size="sm"
                                                        onClick={removeMerchantLogo}
                                                        data-testid="gg-merchant-logo-remove-btn"
                                                        className="text-muted-foreground hover:text-red-500"
                                                    >
                                                        {t('giftGenerator.removeImage')}
                                                    </Button>
                                                )}
                                                <Button
                                                    type="button" variant="ghost" size="sm"
                                                    onClick={() => openLibrary('merchant_logo', (url) => setGgMerchantLogoUrl(url))}
                                                    data-testid="gg-merchant-logo-library-btn"
                                                >
                                                    <ImageIcon className="w-3 h-3 mr-1" /> {t('brandLibrary.openPicker')}
                                                </Button>
                                                <RemoveBackgroundButton
                                                    url={ggMerchantLogoUrl} kind="merchant_logo"
                                                    onApply={(u) => setGgMerchantLogoUrl(u)}
                                                    testId="gg-merchant-logo-removebg-btn" />
                                                <span className="text-[11px] text-muted-foreground" data-testid="gg-merchant-logo-source">
                                                    {ggMerchantLogoUrl
                                                        ? t('giftGenerator.merchantLogoCustom')
                                                        : t('giftGenerator.merchantLogoInherited')}
                                                </span>
                                            </div>
                                            <UploadProgress state={uploadState.merchant} testId="gg-merchant-logo-progress" />
                                            {/* This upload already lands in the Brand library. */}
                                            <p className="text-[11px] text-muted-foreground" data-testid={'gg-merchant-logo-library-note'}>
                                                {t('brandLibrary.savedNote')}
                                            </p>
                                            {(ggMerchantLogoUrl || logoUrl || uploadState.merchant) ? (
                                                <>
                                                    <div className="rounded-lg border border-border overflow-hidden bg-slate-900 flex items-center justify-center p-4">
                                                        <img src={assetSrc('merchant', ggMerchantLogoUrl) || logoUrl} alt="" className="h-auto w-auto max-w-full object-contain" style={{ maxHeight: `${ggLogoSizePx}px` }} data-testid="gg-logo-size-preview" />
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <Label className="text-xs text-muted-foreground">{t('vouchers.logoSize', 'Logo Size')}</Label>
                                                        <span className="text-xs text-muted-foreground">{ggLogoSizePx}px</span>
                                                    </div>
                                                    <input type="range" min={20} max={240} value={ggLogoSizePx}
                                                        onChange={(e) => setGgLogoSizePx(parseInt(e.target.value))}
                                                        className="w-full accent-fuchsia-500" data-testid="gg-logo-size-slider" />
                                                    <div className="flex justify-between text-xs text-muted-foreground">
                                                        <span>{t('vouchers.small', 'Small')}</span>
                                                        <span>{t('vouchers.large', 'Large')}</span>
                                                    </div>
                                                    <p className="text-[11px] text-muted-foreground">{t('giftGenerator.logoSizeSaveNote', 'Click Save Settings to apply this to the live guest game.')}</p>
                                                </>
                                            ) : (
                                                <div className="rounded-lg border border-dashed border-border bg-slate-900/40 px-3 py-4 text-center" data-testid="gg-logo-size-nologo">
                                                    <p className="text-xs text-muted-foreground">{t('giftGenerator.logoSizeNoLogo', 'No logo uploaded yet. Upload a business logo in the Logo section of these voucher settings to show and resize it in the game (otherwise the game shows your business name as text).')}</p>
                                                </div>
                                            )}
                                        </div>


                                        {/* Guest wallpaper */}
                                        <div className="rounded-lg border border-border bg-muted/30 px-3 py-3 space-y-3" data-testid="gg-wallpaper-card">
                                            <div>
                                                <Label className="text-sm font-medium">{t('giftGenerator.wallpaperTitle')}</Label>
                                                <p className="text-xs text-muted-foreground mt-0.5">{t('giftGenerator.wallpaperHint')}</p>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="w-20 h-28 rounded-lg border border-border overflow-hidden bg-slate-900 shrink-0" data-testid="gg-wallpaper-preview">
                                                    <img
                                                        src={assetSrc('wallpaper', ggWallpaperUrl) || '/branding/gg-default-wallpaper.jpg'}
                                                        alt=""
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                                <div className="flex flex-col gap-1.5">
                                                    <span className="text-[11px] text-muted-foreground">
                                                        {ggWallpaperUrl ? t('giftGenerator.wallpaperTitle') : t('giftGenerator.wallpaperDefault')}
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            id="gg-wallpaper-input"
                                                            type="file"
                                                            accept="image/*"
                                                            className="hidden"
                                                            onChange={(e) => uploadGiftWallpaper(e.target.files?.[0])}
                                                            data-testid="gg-wallpaper-input"
                                                        />
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => document.getElementById('gg-wallpaper-input')?.click()}
                                                            disabled={ggWallpaperUploading}
                                                            data-testid="gg-wallpaper-upload-btn"
                                                        >
                                                            {ggWallpaperUploading
                                                                ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                                : <Upload className="w-3 h-3 mr-1" />}
                                                            {ggWallpaperUrl ? t('giftGenerator.replaceImage') : t('giftGenerator.uploadWallpaper')}
                                                        </Button>
                                                        {ggWallpaperUrl && (
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={removeGiftWallpaper}
                                                                data-testid="gg-wallpaper-remove-btn"
                                                                className="text-muted-foreground hover:text-red-500"
                                                            >
                                                                {t('giftGenerator.removeImage')}
                                                            </Button>
                                                        )}
                                                        <Button
                                                            type="button" variant="ghost" size="sm"
                                                            onClick={() => openLibrary('wallpaper', (url) => setGgWallpaperUrl(url))}
                                                            data-testid="gg-wallpaper-library-btn"
                                                        >
                                                            <ImageIcon className="w-3 h-3 mr-1" /> {t('brandLibrary.openPicker')}
                                                        </Button>
                                                    </div>
                                                    <UploadProgress state={uploadState.wallpaper} testId="gg-wallpaper-progress" />
                                                    {/* This upload already lands in the Brand library. */}
                                                    <p className="text-[11px] text-muted-foreground" data-testid={'gg-wallpaper-library-note'}>
                                                        {t('brandLibrary.savedNote')}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Gift voucher design mode */}
                                        <div className="rounded-lg border border-border bg-muted/30 px-3 py-3 space-y-2" data-testid="gg-voucher-design">
                                            <Label className="text-sm font-medium">{t('giftGenerator.voucherDesignTitle')}</Label>
                                            {[
                                                { id: 'existing', label: t('giftGenerator.voucherDesignExisting'), desc: t('giftGenerator.voucherDesignExistingDesc') },
                                                { id: 'custom', label: t('giftGenerator.voucherDesignCustom'), desc: t('giftGenerator.voucherDesignCustomDesc') },
                                            ].map(opt => (
                                                <button
                                                    key={opt.id}
                                                    type="button"
                                                    onClick={() => setGgVoucherDesignMode(opt.id)}
                                                    data-testid={`gg-voucher-mode-${opt.id}`}
                                                    className={'w-full text-left flex items-start gap-3 rounded-lg border-2 px-3 py-2 transition-colors ' + (ggVoucherDesignMode === opt.id ? 'border-fuchsia-500 bg-fuchsia-500/5' : 'border-border hover:border-fuchsia-300')}
                                                >
                                                    <span className={'mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 ' + (ggVoucherDesignMode === opt.id ? 'border-fuchsia-500 bg-fuchsia-500' : 'border-muted-foreground')} />
                                                    <span>
                                                        <span className="block text-sm font-semibold">{opt.label}</span>
                                                        <span className="block text-xs text-muted-foreground">{opt.desc}</span>
                                                    </span>
                                                </button>
                                            ))}
                                            {ggVoucherDesignMode === 'custom' && (
                                                <div className="grid grid-cols-3 gap-2 pt-1" data-testid="gg-voucher-custom-colors">
                                                    {[
                                                        { key: 'bg', label: t('giftGenerator.colorBackground'), val: ggVoucherBg, set: setGgVoucherBg, fallback: '#0f172a' },
                                                        { key: 'text', label: t('giftGenerator.colorText'), val: ggVoucherText, set: setGgVoucherText, fallback: '#ffffff' },
                                                        { key: 'accent', label: t('giftGenerator.colorAccent'), val: ggVoucherAccent, set: setGgVoucherAccent, fallback: '#eab308' },
                                                    ].map(c => (
                                                        <div key={c.key} className="space-y-1">
                                                            <Label className="text-[11px] text-muted-foreground">{c.label}</Label>
                                                            <div className="flex items-center gap-1.5">
                                                                <input
                                                                    type="color"
                                                                    value={c.val || c.fallback}
                                                                    onChange={(e) => c.set(e.target.value)}
                                                                    className="w-8 h-8 rounded border border-border bg-transparent cursor-pointer shrink-0"
                                                                    data-testid={`gg-voucher-${c.key}-picker`}
                                                                />
                                                                <Input
                                                                    value={c.val}
                                                                    onChange={(e) => c.set(e.target.value)}
                                                                    placeholder={c.fallback}
                                                                    className="h-8 text-xs font-mono"
                                                                    data-testid={`gg-voucher-${c.key}-input`}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Gift outcomes */}
                                        <div className="flex items-center justify-between">
                                            <Label className="text-sm font-medium">{t('giftGenerator.gifts')}</Label>
                                            <Badge variant="outline" className="text-xs">{ggGifts.length}/20</Badge>
                                        </div>

                                        {ggGifts.length === 0 && (
                                            <p className="text-xs text-muted-foreground italic" data-testid="gg-empty">{t('giftGenerator.noGiftsYet')}</p>
                                        )}

                                        <div className="space-y-3" data-testid="gg-gift-list">
                                            {ggGifts.map((g, idx) => (
                                                <div key={g.id} className="rounded-xl border border-border bg-muted/20 p-3 space-y-3" data-testid={`gg-gift-${idx}`}>
                                                    <div className="flex items-start gap-2">
                                                        <Input
                                                            value={g.title}
                                                            onChange={(e) => updateGift(idx, { title: e.target.value })}
                                                            placeholder={t('giftGenerator.giftTitle')}
                                                            maxLength={120}
                                                            className="flex-1 font-medium"
                                                            data-testid={`gg-gift-title-${idx}`}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => removeGift(idx)}
                                                            title={t('giftGenerator.remove')}
                                                            data-testid={`gg-gift-remove-${idx}`}
                                                            className="text-muted-foreground hover:text-red-500 mt-2.5 shrink-0"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                    <Input
                                                        value={g.description}
                                                        onChange={(e) => updateGift(idx, { description: e.target.value })}
                                                        placeholder={t('giftGenerator.giftDescription')}
                                                        maxLength={400}
                                                        className="text-sm"
                                                        data-testid={`gg-gift-desc-${idx}`}
                                                    />
                                                    {/* Gift image (optional) — belongs to this gift, not the org logo */}
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-14 h-14 rounded-lg border border-border bg-muted/40 overflow-hidden flex items-center justify-center shrink-0" data-testid={`gg-gift-image-preview-${idx}`}>
                                                            {(g._localPreview || g.image_url) ? (
                                                                <img src={g._localPreview || g.image_url} alt="" className="w-full h-full object-cover" />
                                                            ) : (
                                                                <ImageIcon className="w-5 h-5 text-muted-foreground" />
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col gap-1">
                                                            <Label className="text-[11px] text-muted-foreground">{t('giftGenerator.giftImage')}</Label>
                                                            <div className="flex items-center gap-2">
                                                                <input
                                                                    id={`gg-gift-img-input-${idx}`}
                                                                    type="file"
                                                                    accept="image/*"
                                                                    className="hidden"
                                                                    onChange={(e) => uploadGiftImage(idx, e.target.files?.[0])}
                                                                    data-testid={`gg-gift-image-input-${idx}`}
                                                                />
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => document.getElementById(`gg-gift-img-input-${idx}`)?.click()}
                                                                    disabled={g._imgUploading}
                                                                    data-testid={`gg-gift-image-upload-${idx}`}
                                                                >
                                                                    {g._imgUploading
                                                                        ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                                        : <Upload className="w-3 h-3 mr-1" />}
                                                                    {(g.image_url || g._localPreview) ? t('giftGenerator.replaceImage') : t('giftGenerator.uploadImage')}
                                                                </Button>
                                                                {(g.image_url || g._localPreview) && (
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => removeGiftImage(idx)}
                                                                        data-testid={`gg-gift-image-remove-${idx}`}
                                                                        className="text-muted-foreground hover:text-red-500"
                                                                    >
                                                                        {t('giftGenerator.removeImage')}
                                                                    </Button>
                                                                )}
                                                                <Button
                                                                    type="button" variant="ghost" size="sm"
                                                                    onClick={() => openLibrary('gift', (url) => applyGiftImage(idx, 'single', url))}
                                                                    data-testid={`gg-gift-image-library-${idx}`}
                                                                >
                                                                    <ImageIcon className="w-3 h-3 mr-1" /> {t('brandLibrary.openPicker')}
                                                                </Button>
                                                            </div>
                                                            <UploadProgress state={uploadState[`gift-${idx}-single`]} testId={`gg-gift-image-progress-${idx}`} />
                                                            {/* This upload already lands in the Brand library. */}
                                                            <p className="text-[11px] text-muted-foreground" data-testid={`gg-gift-image-library-note-${idx}`}>
                                                                {t('brandLibrary.savedNote')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {/* Doubled-prize image — shown ONLY on the Lead Generation
                                                        interstitial. Falls back to the generic 2x graphic.
                                                        With Lead Generation OFF for this setup the block
                                                        collapses to one line (a card-level override can still
                                                        turn 2x on, so it is never hidden and never cleared). */}
                                                    {(!ggLeadGenEnabled && !dblExpanded[g.id]) ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => setDblExpanded((s) => ({ ...s, [g.id]: true }))}
                                                            className="w-full flex items-center gap-2 text-left text-[11px] text-muted-foreground hover:text-foreground transition-colors rounded-md border border-dashed border-border px-2.5 py-2"
                                                            data-testid={`gg-gift-double-image-collapsed-${idx}`}
                                                        >
                                                            <ChevronRight className="w-3 h-3 shrink-0" />
                                                            <span>{t('giftGenerator.doubleImageCollapsed')}</span>
                                                            {(g.double_image_url || g._localDoublePreview) && (
                                                                <span className="ml-auto shrink-0 text-[10px] font-semibold text-amber-500" data-testid={`gg-gift-double-image-saved-${idx}`}>
                                                                    {t('giftGenerator.doubleImageSaved')}
                                                                </span>
                                                            )}
                                                        </button>
                                                    ) : (
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-14 h-14 rounded-lg border border-amber-400/40 bg-amber-400/5 overflow-hidden flex items-center justify-center shrink-0" data-testid={`gg-gift-double-image-preview-${idx}`}>
                                                            {(g._localDoublePreview || g.double_image_url) ? (
                                                                <img src={g._localDoublePreview || g.double_image_url} alt="" className="w-full h-full object-cover" />
                                                            ) : (
                                                                <ImageIcon className="w-5 h-5 text-amber-500/70" />
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col gap-1">
                                                            <Label className="text-[11px] text-muted-foreground">{t('giftGenerator.doubleImage')}</Label>
                                                            <div className="flex items-center gap-2">
                                                                <input
                                                                    id={`gg-gift-double-img-input-${idx}`}
                                                                    type="file"
                                                                    accept="image/*"
                                                                    className="hidden"
                                                                    onChange={(e) => uploadGiftImage(idx, e.target.files?.[0], 'double')}
                                                                    data-testid={`gg-gift-double-image-input-${idx}`}
                                                                />
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => document.getElementById(`gg-gift-double-img-input-${idx}`)?.click()}
                                                                    disabled={g._dblUploading}
                                                                    data-testid={`gg-gift-double-image-upload-${idx}`}
                                                                >
                                                                    {g._dblUploading
                                                                        ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                                        : <Upload className="w-3 h-3 mr-1" />}
                                                                    {(g.double_image_url || g._localDoublePreview) ? t('giftGenerator.replaceImage') : t('giftGenerator.uploadImage')}
                                                                </Button>
                                                                {(g.double_image_url || g._localDoublePreview) && (
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => removeGiftDoubleImage(idx)}
                                                                        data-testid={`gg-gift-double-image-remove-${idx}`}
                                                                        className="text-muted-foreground hover:text-red-500"
                                                                    >
                                                                        {t('giftGenerator.removeImage')}
                                                                    </Button>
                                                                )}
                                                                <Button
                                                                    type="button" variant="ghost" size="sm"
                                                                    onClick={() => openLibrary('gift_double,gift', (url) => applyGiftImage(idx, 'double', url))}
                                                                    data-testid={`gg-gift-double-image-library-${idx}`}
                                                                >
                                                                    <ImageIcon className="w-3 h-3 mr-1" /> {t('brandLibrary.openPicker')}
                                                                </Button>
                                                            </div>
                                                            <p className="text-[10px] text-muted-foreground/80" data-testid={`gg-gift-double-image-hint-${idx}`}>
                                                                {t('giftGenerator.doubleImageHint')}
                                                            </p>
                                                            <UploadProgress state={uploadState[`gift-${idx}-double`]} testId={`gg-gift-double-image-progress-${idx}`} />
                                                            {/* This upload already lands in the Brand library. */}
                                                            <p className="text-[11px] text-muted-foreground" data-testid={`gg-gift-double-image-library-note-${idx}`}>
                                                                {t('brandLibrary.savedNote')}
                                                            </p>
                                                            {!ggLeadGenEnabled && (
                                                                <p className="text-[10px] text-amber-500/90" data-testid={`gg-gift-double-image-offnote-${idx}`}>
                                                                    {t('giftGenerator.doubleImageLeadGenOff')}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    )}
                                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
                                                        <div className="space-y-1 col-span-2 sm:col-span-1">
                                                            <Label className="text-[11px] text-muted-foreground">{t('giftGenerator.giftType')}</Label>
                                                            <Select value={g.gift_type} onValueChange={(v) => updateGift(idx, { gift_type: v })}>
                                                                <SelectTrigger data-testid={`gg-gift-type-${idx}`}>
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="non_monetary">{t('giftGenerator.nonMonetary')}</SelectItem>
                                                                    <SelectItem value="monetary">{t('giftGenerator.monetary')}</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        {g.gift_type === 'monetary' && (
                                                            <div className="space-y-1">
                                                                <Label className="text-[11px] text-muted-foreground">{t('giftGenerator.giftValue')}</Label>
                                                                <Input
                                                                    type="number"
                                                                    min={0}
                                                                    value={g.value}
                                                                    onChange={(e) => updateGift(idx, { value: Math.max(0, parseFloat(e.target.value) || 0) })}
                                                                    data-testid={`gg-gift-value-${idx}`}
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="space-y-1">
                                                            <Label className="text-[11px] text-muted-foreground">{t('giftGenerator.quantity')}</Label>
                                                            <Input
                                                                type="number"
                                                                min={0}
                                                                value={g.quantity}
                                                                onChange={(e) => updateGift(idx, { quantity: Math.max(0, parseInt(e.target.value) || 0) })}
                                                                data-testid={`gg-gift-qty-${idx}`}
                                                            />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <Label className="text-[11px] text-muted-foreground">{t('giftGenerator.remaining')}</Label>
                                                            <div className="h-9 flex items-center px-2 text-sm font-medium text-foreground" data-testid={`gg-gift-remaining-${idx}`}>
                                                                {g.remaining === undefined ? g.quantity : g.remaining}
                                                                <span className="text-muted-foreground font-normal">&nbsp;/&nbsp;{g.quantity}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* This gift's own Save — it writes straight to the
                                                        organization and setup shown above. */}
                                                    <div className="flex flex-wrap items-center gap-2 pt-1">
                                                        <Button size="sm" onClick={() => saveGift(idx)}
                                                                disabled={!!giftSaving[idx]}
                                                                data-testid={`gg-gift-save-${idx}`}>
                                                            <Save className="w-4 h-4 mr-2" />
                                                            {giftSaving[idx] ? t('giftGenerator.giftSaving')
                                                                : t('giftGenerator.saveGift')}
                                                        </Button>
                                                        {!goalLink.known(ggSetupId, g.id) && (
                                                            <span className="text-[11px] text-muted-foreground"
                                                                  data-testid={`gg-gift-unsaved-${idx}`}>
                                                                {t('giftGenerator.giftNotSavedYet')}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {/* One button, on the gift itself. Shown whenever
                                                        this organization runs Tap’N Win; a gift that
                                                        has not been saved yet says so. */}
                                                    {goalLink.available && g.id && (
                                                        <div className="flex items-center gap-2 pt-1" data-testid={`gg-gift-goal-${idx}`}>
                                                            {goalLink.known(ggSetupId, g.id) ? (
                                                                <AddToGoalGameButton link={goalLink} setupId={ggSetupId}
                                                                                     giftId={g.id}
                                                                                     testid={`gg-gift-add-to-goal-${idx}`} />
                                                            ) : (
                                                                <span className="text-[11px] text-muted-foreground"
                                                                      data-testid={`gg-gift-add-to-goal-${idx}-unsaved`}>
                                                                    {t('goalGame.giftSaveFirst')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={addGift}
                                            disabled={ggGifts.length >= 20}
                                            data-testid="gg-add-gift-btn"
                                        >
                                            <Plus className="w-3.5 h-3.5 mr-1" /> {t('giftGenerator.addGift')}
                                        </Button>
                                    </CardContent>
                                )}
                            </Card>

                            {/* Logo Upload */}
                            <Card data-insight-id="vouchers.setting.logo">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <ImageIcon className="w-4 h-4 text-amber-500" />
                                        {t('vouchers.organizationLogo')}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center gap-4">
                                        <div className="w-24 h-24 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center bg-muted/30 overflow-hidden">
                                            {assetSrc('logo', logoUrl) ? (
                                                <img src={assetSrc('logo', logoUrl)} alt="Logo" className="w-full h-full object-contain p-2" data-testid="voucher-logo-preview" />
                                            ) : (
                                                <Upload className="w-8 h-8 text-muted-foreground/40" />
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" />
                                            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-logo-btn">
                                                {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                                                {logoUrl ? t('vouchers.replaceLogo') : t('vouchers.uploadLogo')}
                                            </Button>
                                            <UploadProgress state={uploadState.logo} testId="voucher-logo-progress" />
                                            {/* This upload already lands in the Brand library. */}
                                            <p className="text-[11px] text-muted-foreground" data-testid={'voucher-logo-library-note'}>
                                                {t('brandLibrary.savedNote')}
                                            </p>
                                            <Button
                                                variant="ghost" size="sm"
                                                onClick={() => openLibrary('logo', setVoucherLogoFromLibrary)}
                                                data-testid="voucher-logo-library-btn"
                                            >
                                                <ImageIcon className="w-3 h-3 mr-1" /> {t('brandLibrary.openPicker')}
                                            </Button>
                                            <RemoveBackgroundButton
                                                url={logoUrl} kind="org_logo"
                                                onApply={(u) => setLogoUrl(u)}
                                                testId="voucher-logo-removebg-btn" />
                                            <p className="text-xs text-muted-foreground">{t('vouchers.logoFileTypes')}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Voucher Design — colors & presets */}
                            <Card data-insight-id="vouchers.setting.design" data-testid="voucher-design-card">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Palette className="w-4 h-4 text-blue-500" />
                                        {t('vouchers.design.title', 'Voucher Design')}
                                    </CardTitle>
                                    <p className="text-xs text-muted-foreground">
                                        {t('vouchers.design.hint', 'Pick a preset or set custom colors for the generated gift voucher. These apply to every voucher your organization issues.')}
                                    </p>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {/* Typography — shared with Tap’N Win, saved per gift setup */}
                                    <div data-testid="voucher-typography">
                                        <Label className="text-xs text-muted-foreground">
                                            {t('vouchers.design.typography', 'Fonts & text size')}
                                        </Label>
                                        <p className="text-[11px] text-muted-foreground mt-0.5">
                                            {t('vouchers.design.typographyHint', 'Saved with the selected gift setup. Leave on Default to keep the current look.')}
                                        </p>
                                        <div className="grid grid-cols-2 gap-2 mt-2">
                                            <FontPicker
                                                value={ggHeadlineFont}
                                                onChange={setGgHeadlineFont}
                                                label={t('vouchers.design.headlineFont', 'Headline')}
                                                testid="voucher-headline-font"
                                            />
                                            <FontPicker
                                                value={ggBodyFont}
                                                onChange={setGgBodyFont}
                                                label={t('vouchers.design.bodyFont', 'Body')}
                                                testid="voucher-body-font"
                                            />
                                            <select value={ggHeadlineSize} onChange={(e) => setGgHeadlineSize(e.target.value)}
                                                    data-testid="voucher-headline-size"
                                                    className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs">
                                                {TEXT_SIZES.map((z) => (
                                                    <option key={z.key} value={z.key}>{t('vouchers.design.headlineSize', 'Headline size')}: {z.label}</option>
                                                ))}
                                            </select>
                                            <select value={ggBodySize} onChange={(e) => setGgBodySize(e.target.value)}
                                                    data-testid="voucher-body-size"
                                                    className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs">
                                                {TEXT_SIZES.map((z) => (
                                                    <option key={z.key} value={z.key}>{t('vouchers.design.bodySize', 'Body size')}: {z.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Fine control. 100% is exactly the step above. */}
                                        <div className="grid grid-cols-2 gap-3 mt-3">
                                            {[
                                                ['headline', t('vouchers.design.headlineFine', 'Headline fine'),
                                                 ggHeadlineScale, setGgHeadlineScale],
                                                ['body', t('vouchers.design.bodyFine', 'Body fine'),
                                                 ggBodyScale, setGgBodyScale],
                                            ].map(([key, labelText, val, setVal]) => (
                                                <div key={key} className="space-y-1">
                                                    <div className="flex items-center justify-between">
                                                        <Label className="text-[11px] text-muted-foreground">{labelText}</Label>
                                                        <span className="text-[11px] tabular-nums text-muted-foreground"
                                                              data-testid={`voucher-${key}-scale-value`}>
                                                            {val}%
                                                        </span>
                                                    </div>
                                                    <input
                                                        type="range" min={SCALE_MIN} max={SCALE_MAX} step={5}
                                                        value={val}
                                                        onChange={(e) => setVal(parseInt(e.target.value, 10))}
                                                        data-testid={`voucher-${key}-scale`}
                                                        className="w-full accent-emerald-500"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Presets */}
                                    <div>
                                        <Label className="text-xs text-muted-foreground">{t('vouchers.design.presets', 'Presets')}</Label>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid="voucher-design-presets">
                                            {VOUCHER_DESIGN_PRESETS.map((p) => {
                                                const isActive = activeDesignPreset?.key === p.key;
                                                return (
                                                    <button
                                                        key={p.key}
                                                        type="button"
                                                        onClick={() => applyDesignPreset(p)}
                                                        data-testid={`voucher-design-preset-${p.key}`}
                                                        className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-xs font-medium transition-all ${
                                                            isActive
                                                                ? 'border-blue-500 ring-1 ring-blue-500 bg-blue-500/10 text-foreground'
                                                                : 'border-border bg-muted/40 text-muted-foreground hover:bg-muted'
                                                        }`}
                                                    >
                                                        <span className="flex -space-x-1 shrink-0">
                                                            {p.swatch.map((c, i) => (
                                                                <span key={i} className="w-3.5 h-3.5 rounded-full border border-white/40" style={{ backgroundColor: c }} />
                                                            ))}
                                                        </span>
                                                        {t(`vouchers.design.preset.${p.key}`, p.label)}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Color pickers */}
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        {[
                                            { key: 'bg', label: t('vouchers.design.background', 'Background'), value: voucherBg, set: setVoucherBg, fallback: VOUCHER_DESIGN_DEFAULTS.bg },
                                            { key: 'text', label: t('vouchers.design.text', 'Text'), value: voucherText, set: setVoucherText, fallback: VOUCHER_DESIGN_DEFAULTS.text },
                                            { key: 'accent', label: t('vouchers.design.accent', 'Accent'), value: voucherAccent, set: setVoucherAccent, fallback: VOUCHER_DESIGN_DEFAULTS.accent },
                                        ].map((f) => (
                                            <div key={f.key} className="space-y-1.5">
                                                <Label className="text-xs text-muted-foreground">{f.label}</Label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="color"
                                                        value={f.value || f.fallback}
                                                        onChange={(e) => f.set(e.target.value)}
                                                        data-testid={`voucher-design-${f.key}-picker`}
                                                        className="w-9 h-9 rounded-md border border-border bg-transparent cursor-pointer p-0.5"
                                                    />
                                                    <Input
                                                        value={f.value}
                                                        onChange={(e) => f.set(e.target.value)}
                                                        placeholder={t('vouchers.design.default', 'Default')}
                                                        data-testid={`voucher-design-${f.key}-input`}
                                                        className="flex-1 font-mono text-xs uppercase"
                                                    />
                                                    {f.value && (
                                                        <button
                                                            type="button"
                                                            onClick={() => f.set('')}
                                                            title={t('vouchers.design.clear', 'Reset to default')}
                                                            data-testid={`voucher-design-${f.key}-clear`}
                                                            className="text-muted-foreground hover:text-red-500 shrink-0"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Contrast guard */}
                                    {designLowContrast && (
                                        <div
                                            className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600"
                                            data-testid="voucher-design-contrast-warning"
                                        >
                                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                            <span>
                                                {t('vouchers.design.contrastWarning', 'Low contrast between text and background ({{ratio}}:1). Aim for at least 4.5:1 so the voucher stays readable.', { ratio: designContrast.toFixed(1) })}
                                            </span>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Preset Amounts */}
                            <Card data-insight-id="vouchers.setting.preset-amounts">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <DollarSign className="w-4 h-4 text-emerald-500" />
                                        {t('vouchers.presetAmounts')}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <p className="text-xs text-muted-foreground">{t('vouchers.presetAmountsHint')}</p>
                                    <div className="flex flex-wrap gap-2" data-testid="preset-amounts-list">
                                        {presetAmounts.map(amt => (
                                            <Badge
                                                key={amt}
                                                variant="outline"
                                                className={`text-sm px-3 py-1.5 gap-1 cursor-pointer transition-colors ${
                                                    previewAmount === amt
                                                        ? 'bg-emerald-500 text-white border-emerald-500'
                                                        : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/20'
                                                }`}
                                                onClick={() => setPreviewAmount(amt)}
                                                data-testid={`preset-amount-${amt}`}
                                            >
                                                ${amt}
                                                <button onClick={(e) => { e.stopPropagation(); removeAmount(amt); }} className="ml-1 hover:text-red-500">
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <Input type="number" placeholder={t('vouchers.customAmount')} value={newAmount}
                                            onChange={(e) => setNewAmount(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && addAmount()}
                                            className="w-32" data-testid="add-amount-input" />
                                        <Button variant="outline" size="sm" onClick={addAmount} data-testid="add-amount-btn">
                                            <Plus className="w-3 h-3 mr-1" /> {t('common.add')}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Expiry Options */}
                            <Card data-insight-id="vouchers.setting.expiry">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-orange-500" />
                                        {t('vouchers.expiryConfiguration')}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div>
                                        <Label className="text-xs text-muted-foreground">{t('vouchers.expiryHint')}</Label>
                                        <div className="flex flex-wrap gap-2 mt-2" data-testid="preset-expiry-list">
                                            {presetExpiry.map(days => (
                                                <Badge
                                                    key={days} variant="outline"
                                                    className={`text-sm px-3 py-1.5 gap-1 cursor-pointer transition-colors ${
                                                        previewExpiry === days
                                                            ? 'bg-orange-500 text-white border-orange-500'
                                                            : 'bg-orange-500/10 text-orange-600 border-orange-500/30 hover:bg-orange-500/20'
                                                    }`}
                                                    onClick={() => { setPreviewExpiry(days); setDefaultExpiry(days); }}
                                                    data-testid={`preset-expiry-${days}`}
                                                >
                                                    {t('vouchers.nDays', { count: days })}
                                                    <button onClick={(e) => { e.stopPropagation(); removeExpiry(days); }} className="ml-1 hover:text-red-500">
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </Badge>
                                            ))}
                                        </div>
                                        <div className="flex gap-2 mt-2">
                                            <Input type="number" placeholder={t('vouchers.customDays')} value={newExpiry}
                                                onChange={(e) => setNewExpiry(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && addExpiry()}
                                                className="w-32" data-testid="add-expiry-input" />
                                            <Button variant="outline" size="sm" onClick={addExpiry} data-testid="add-expiry-btn">
                                                <Plus className="w-3 h-3 mr-1" /> {t('common.add')}
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="pt-2 border-t">
                                        <Label className="text-xs text-muted-foreground">{t('vouchers.defaultExpiryPeriod')}</Label>
                                        <div className="flex items-center gap-2 mt-1">
                                            <Input type="number" value={defaultExpiry}
                                                onChange={(e) => setDefaultExpiry(parseInt(e.target.value) || 30)}
                                                className="w-24" min={1} max={365} data-testid="default-expiry-input" />
                                            <span className="text-sm text-muted-foreground">{t('vouchers.days')}</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Default Message */}
                            <Card data-insight-id="vouchers.setting.default-message">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <MessageSquare className="w-4 h-4 text-violet-500" />
                                        {t('vouchers.defaultMessage')}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <Textarea value={defaultMessage}
                                        onChange={(e) => setDefaultMessage(e.target.value)}
                                        placeholder={t('vouchers.defaultMessagePlaceholder')}
                                        rows={2} maxLength={200} data-testid="default-message-input" />
                                    <p className="text-xs text-muted-foreground mt-1">{t('vouchers.defaultMessageHint')}</p>
                                </CardContent>
                            </Card>

                            {/* Reason Selector */}
                            <Card data-insight-id="vouchers.setting.reasons">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Gift className="w-4 h-4 text-violet-500" />
                                        {t('vouchers.reasonSelector', 'Voucher Reasons')}
                                    </CardTitle>
                                    <p className="text-xs text-muted-foreground">
                                        {t('vouchers.reasonSelectorHint', 'Select a reason to preview the stripe color. This is the list staff and managers use during issuance.')}
                                    </p>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 gap-2" data-testid="reason-selector">
                                        {VOUCHER_REASONS.map(r => {
                                            const style = STRIPE_BUTTON_STYLES[r.stripe] || STRIPE_BUTTON_STYLES.green;
                                            const isSelected = previewReason === r.code;
                                            return (
                                                <button key={r.code} data-testid={`settings-reason-${r.code}`}
                                                    onClick={() => setPreviewReason(r.code)}
                                                    className={`h-9 rounded-lg text-xs font-medium px-3 transition-all border flex items-center gap-1.5 ${
                                                        isSelected ? `${style.bg} ${style.text} border-current ring-1 ${style.ring}`
                                                            : 'bg-muted/40 text-muted-foreground hover:bg-muted border-border'
                                                    }`}
                                                >
                                                    <span className={`w-2 h-2 rounded-full shrink-0 ${style.dot}`} />
                                                    {t(r.labelKey)}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> {t('vouchers.categoryRecovery')}</span>
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> {t('vouchers.categoryRecognition')}</span>
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full gold-gradient-dot" /> {t('vouchers.categoryVip')}</span>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Right column — Live Preview */}
                        <div>
                            <Card className="sticky top-6" data-insight-id="vouchers.setting.live-preview">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Eye className="w-4 h-4 text-blue-500" />
                                        {t('vouchers.livePreview')}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div
                                        className={`design-body-scope rounded-xl overflow-hidden text-white ${designHasBg ? '' : 'bg-gradient-to-b from-slate-900 to-slate-800'}`}
                                        style={{
                                            ...(designHasBg ? { ...previewBgStyle, color: effTextColor } : {}),
                                            fontFamily: fontStack(ggBodyFont) || undefined,
                                            fontSize: bodyFontSize(ggBodySize, ggBodyScale) || undefined,
                                        }}
                                        data-testid="voucher-live-preview"
                                    >
                                        <div className={effAccentColor ? 'h-1.5' : `h-1.5 ${previewStripe.bar}`} style={effAccentColor ? { backgroundColor: effAccentColor } : undefined} />
                                        <div className="p-5 space-y-4">
                                            <div className="text-center">
                                                {logoUrl ? (
                                                    <img src={logoUrl} alt="" className="mx-auto object-contain" style={{ height: `${logoSize}px` }} data-testid="preview-logo-img" />
                                                ) : (
                                                    <div className="h-10 flex items-center justify-center">
                                                        <span className="text-xs text-slate-500 italic">{t('vouchers.noLogoUploaded')}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-center">
                                                <p
                                                    className={effAccentColor ? 'text-4xl font-bold' : `text-4xl font-bold ${previewStripe.value}`}
                                                    style={{
                                                        ...(effAccentColor ? { color: effAccentColor } : {}),
                                                        fontFamily: fontStack(ggHeadlineFont) || undefined,
                                                        fontSize: headlineFontSize(ggHeadlineSize, ggHeadlineScale) || undefined,
                                                    }}
                                                    data-testid="preview-amount"
                                                >${activePreviewAmount}</p>
                                                <p className="text-xs mt-0.5" style={{ color: designHasText ? effTextColor : undefined, opacity: 0.6 }}>{t('vouchers.recoveryVoucher')}</p>
                                            </div>
                                            <div className={effAccentColor ? '-mx-5 h-2' : `-mx-5 h-2 ${previewStripe.bar}`} style={effAccentColor ? { backgroundColor: effAccentColor } : undefined} />
                                            <div className="bg-black/20 rounded-lg py-3 text-center">
                                                <p className="text-xl font-mono font-bold tracking-[0.15em]" style={{ color: designHasText ? effTextColor : '#ffffff' }}>{previewCode}</p>
                                            </div>
                                            {defaultMessage && <p className="text-center text-xs italic" style={{ color: designHasText ? effTextColor : undefined, opacity: 0.8 }}>{defaultMessage}</p>}
                                            <div className="text-center">
                                                <p
                                                    className={effAccentColor ? 'text-xs' : `text-xs ${previewStripe.accent}`}
                                                    style={effAccentColor ? { color: effAccentColor } : undefined}
                                                    data-testid="preview-expiry"
                                                >
                                                    {t('vouchers.validUntil', { date: previewExpiryDate })}
                                                </p>
                                            </div>
                                        </div>
                                        <div className={effAccentColor ? 'h-1' : `h-1 ${previewStripe.bar}`} style={effAccentColor ? { backgroundColor: effAccentColor } : undefined} />
                                    </div>

                                    {logoUrl && (
                                        <div className="mt-4 space-y-2" data-testid="logo-size-control">
                                            <div className="flex items-center justify-between">
                                                <Label className="text-xs text-muted-foreground">{t('vouchers.logoSize')}</Label>
                                                <span className="text-xs text-muted-foreground">{logoSize}px</span>
                                            </div>
                                            <input type="range" min={20} max={240} value={logoSize}
                                                onChange={(e) => setLogoSize(parseInt(e.target.value))}
                                                className="w-full accent-emerald-500" data-testid="logo-size-slider" />
                                            <div className="flex justify-between text-xs text-muted-foreground">
                                                <span>{t('vouchers.small')}</span>
                                                <span>{t('vouchers.large')}</span>
                                            </div>
                                        </div>
                                    )}
                                    <p className="text-xs text-muted-foreground text-center mt-3">{t('vouchers.previewNote')}</p>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    {/* Voucher Allocation Section */}
                    <Card data-testid="voucher-allocation-section" data-insight-id="vouchers.action.allocate-bulk">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Users className="w-4 h-4 text-violet-500" />
                                {t('vouchers.allocationTitle')}
                            </CardTitle>
                            <p className="text-xs text-muted-foreground">{t('vouchers.allocationSubtitle')}</p>
                            {/* Game Mode takes its prizes from the Gift Generator setup above, so
                                this section starts collapsed to stop the two being confused. */}
                            {allocCollapsible && (
                                <button
                                    type="button"
                                    onClick={() => setAllocExpanded((v) => !v)}
                                    data-testid="voucher-allocation-toggle"
                                    className="mt-2 w-full text-left flex items-start gap-2 rounded-lg border border-amber-400/40 bg-amber-500/5 px-3 py-2 hover:bg-amber-500/10 transition-colors"
                                >
                                    <Info className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                                    <span className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed flex-1" data-testid="voucher-allocation-game-note">
                                        {t('vouchers.allocationGameNote', 'Not used by the Gift Generator game. Game prizes are set in Gift Generator setup above. Open this only to issue regular vouchers.')}
                                    </span>
                                    <ChevronDown className={'w-4 h-4 text-amber-500 shrink-0 mt-0.5 transition-transform ' + (allocExpanded ? 'rotate-180' : '')} />
                                </button>
                            )}
                        </CardHeader>
                        {allocOpen && (
                        <CardContent className="space-y-4">
                            {isSuperAdmin && organizations.length > 1 && (
                                <div className="space-y-1" data-testid="alloc-org-wrapper">
                                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Building2 className="w-3 h-3" /> {t('common.organization')}
                                    </Label>
                                    <Select value={allocOrg} disabled={!!scopeOrgId} onValueChange={(v) => {
                                        setAllocOrg(v); setAllocLocation(''); setAllocRecipient(''); setRecipients([]);
                                    }}>
                                        <SelectTrigger data-testid="alloc-org-select">
                                            <SelectValue placeholder={t('vouchers.selectOrganization')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {organizations.map(org => (
                                                <SelectItem key={org.id} value={org.id}>{org.name || org.display_name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                                <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                        <MapPin className="w-3 h-3" /> {t('vouchers.location')}
                                    </Label>
                                    <Select value={allocLocation} onValueChange={setAllocLocation}
                                        disabled={isSuperAdmin && organizations.length > 1 && !allocOrg}>
                                        <SelectTrigger data-testid="alloc-location-select">
                                            <SelectValue placeholder={
                                                isSuperAdmin && organizations.length > 1 && !allocOrg
                                                    ? t('vouchers.selectOrgFirst') : t('vouchers.selectLocation')
                                            } />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {locations.map(loc => (<SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Gift className="w-3 h-3" /> {t('vouchers.deliveryMethod')}
                                    </Label>
                                    <Select value={allocDeliveryMethod}
                                        onValueChange={(v) => { setAllocDeliveryMethod(v); setAllocRecipient(''); setAllocEmail(''); setAllocPhone(''); }}>
                                        <SelectTrigger data-testid="alloc-delivery-method-select">
                                            <SelectValue placeholder={t('vouchers.deliveryMethod')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="staff_card"><span className="flex items-center gap-2"><CreditCard className="w-3.5 h-3.5" /> {t('vouchers.staffCard')}</span></SelectItem>
                                            <SelectItem value="email"><span className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> {t('vouchers.email')}</span></SelectItem>
                                            <SelectItem value="sms"><span className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {t('vouchers.sms')}</span></SelectItem>
                                            <SelectItem value="email_and_sms"><span className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> {t('vouchers.emailAndSms')}</span></SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Users className="w-3 h-3" /> {t('vouchers.recipient')}
                                    </Label>
                                    {allocDeliveryMethod === 'staff_card' ? (
                                        <Select value={allocRecipient} onValueChange={setAllocRecipient}
                                            disabled={!allocLocation || loadingRecipients}>
                                            <SelectTrigger data-testid="alloc-recipient-select">
                                                <SelectValue placeholder={
                                                    loadingRecipients ? t('common.loading')
                                                    : !allocLocation ? t('vouchers.selectLocationFirst')
                                                    : recipients.length === 0 ? t('vouchers.generalUseOnly', 'General Use (no staff at this location)')
                                                    : t('vouchers.selectStaffMember')
                                                } />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__general__" data-testid="alloc-recipient-general-use">
                                                    {t('vouchers.generalUse', 'General Use')}
                                                    <span className="text-muted-foreground ml-1 text-xs">({t('vouchers.generalUseHint', 'no employee — org/location pool')})</span>
                                                </SelectItem>
                                                {recipients.map(r => (
                                                    <SelectItem key={r.id} value={r.id}>
                                                        {r.name}
                                                        <span className="text-muted-foreground ml-1 text-xs">({r.type})</span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : allocDeliveryMethod === 'sms' ? (
                                        <Input type="tel" value={allocPhone} onChange={(e) => setAllocPhone(e.target.value)}
                                            placeholder="+15145551234" data-testid="alloc-phone-input" />
                                    ) : allocDeliveryMethod === 'email_and_sms' ? (
                                        <div className="space-y-2">
                                            <Input type="email" value={allocEmail} onChange={(e) => setAllocEmail(e.target.value)}
                                                placeholder="recipient@email.com" data-testid="alloc-email-input" />
                                            <Input type="tel" value={allocPhone} onChange={(e) => setAllocPhone(e.target.value)}
                                                placeholder="+15145551234" data-testid="alloc-phone-input" />
                                        </div>
                                    ) : (
                                        <Input type="email" value={allocEmail} onChange={(e) => setAllocEmail(e.target.value)}
                                            placeholder="recipient@email.com" data-testid="alloc-email-input" />
                                    )}
                                </div>

                                <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Gift className="w-3 h-3" /> {t('vouchers.reason', 'Reason')}
                                    </Label>
                                    <Select value={allocReason} onValueChange={setAllocReason}>
                                        <SelectTrigger data-testid="alloc-reason-select">
                                            <SelectValue placeholder={t('vouchers.reason', 'Reason')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {VOUCHER_REASONS.map(r => {
                                                const style = STRIPE_BUTTON_STYLES[r.stripe] || STRIPE_BUTTON_STYLES.green;
                                                return (
                                                    <SelectItem key={r.code} value={r.code}>
                                                        <span className="flex items-center gap-1.5">
                                                            <span className={`w-2 h-2 rounded-full shrink-0 ${style.dot}`} />
                                                            {t(r.labelKey)}
                                                        </span>
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                        <DollarSign className="w-3 h-3" /> {t('vouchers.amount')}
                                        {!isElevated && <span className="text-muted-foreground/60">(max ${maxVoucherValue})</span>}
                                    </Label>
                                    <Select value={allocAmount} onValueChange={(v) => { setAllocAmount(v); if (v !== 'custom') setAllocCustomAmount(''); }}>
                                        <SelectTrigger data-testid="alloc-amount-select">
                                            <SelectValue placeholder={t('vouchers.amount')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {presetAmounts.map(amt => (<SelectItem key={amt} value={String(amt)}>${amt}</SelectItem>))}
                                            <SelectItem value="custom">{t('vouchers.otherAmount', 'Other amount')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {allocAmount === 'custom' && (
                                        <Input type="number" min={1} max={isElevated ? 10000 : maxVoucherValue} step="0.01"
                                            value={allocCustomAmount} onChange={(e) => setAllocCustomAmount(e.target.value)}
                                            placeholder={`$1 - $${isElevated ? '10,000' : maxVoucherValue}`}
                                            className="mt-1" data-testid="alloc-custom-amount" />
                                    )}
                                </div>

                                <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground">{t('vouchers.quantity')}</Label>
                                    <Input type="number" min={1} max={50} value={allocQty}
                                        onChange={(e) => setAllocQty(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                                        data-testid="alloc-quantity-input" />
                                </div>

                                <div className="col-span-full flex items-start gap-2 mt-1" data-testid="alloc-loyal-review-toggle">
                                    <input
                                        type="checkbox"
                                        id="alloc-available-for-loyal-review"
                                        checked={allocAvailableForLoyalReview}
                                        onChange={(e) => setAllocAvailableForLoyalReview(e.target.checked)}
                                        className="mt-1 h-4 w-4 accent-emerald-500 cursor-pointer"
                                        data-testid="alloc-available-for-loyal-review-checkbox"
                                    />
                                    <Label
                                        htmlFor="alloc-available-for-loyal-review"
                                        className="text-xs text-slate-700 dark:text-slate-300 leading-snug cursor-pointer"
                                    >
                                        {t('loyalReview.merchant.checkbox', 'Available for Loyal Review')}
                                        <span className="block text-[11px] text-muted-foreground font-normal mt-0.5">
                                            {t(
                                                'loyalReview.merchant.checkboxHint',
                                                'Marks this voucher as a reward guests can select when the Review Shield offers the gift-vs-review choice.',
                                            )}
                                        </span>
                                    </Label>
                                </div>

                                <Button onClick={addToAllocation}
                                    disabled={
                                        (!allocAmount || (allocAmount === 'custom' && (!allocCustomAmount || parseFloat(allocCustomAmount) <= 0))) ||
                                        (allocDeliveryMethod === 'staff_card' && !allocRecipient) ||
                                        (allocDeliveryMethod === 'email' && !allocEmail.trim()) ||
                                        (allocDeliveryMethod === 'sms' && !allocPhone.trim()) ||
                                        (allocDeliveryMethod === 'email_and_sms' && (!allocEmail.trim() || !allocPhone.trim()))
                                    }
                                    className="bg-violet-600 hover:bg-violet-700" data-testid="alloc-add-btn">
                                    <Plus className="w-4 h-4 mr-1" />
                                    {t('vouchers.addToList')}
                                </Button>
                            </div>

                            {allocList.length > 0 && (
                                <div className="space-y-3">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('vouchers.recipient')}</TableHead>
                                                <TableHead>{t('vouchers.delivery')}</TableHead>
                                                <TableHead>{t('vouchers.amount')}</TableHead>
                                                <TableHead>{t('vouchers.qty')}</TableHead>
                                                <TableHead>{t('vouchers.total')}</TableHead>
                                                <TableHead className="w-12"></TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {allocList.map((item, idx) => (
                                                <TableRow key={item._key || `alloc-${idx}`} data-testid={`alloc-row-${idx}`}>
                                                    <TableCell className="font-medium text-sm">{item.recipient_name}</TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className={`text-xs ${methodColors[item.delivery_method] || methodColors.staff_card}`}>
                                                            {methodLabels[item.delivery_method] || t('vouchers.staffCard')}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-emerald-600 font-semibold">${item.value}</TableCell>
                                                    <TableCell>{item.quantity}</TableCell>
                                                    <TableCell className="font-semibold">${item.value * item.quantity}</TableCell>
                                                    <TableCell>
                                                        <button onClick={() => removeFromAllocation(idx)}
                                                            className="text-red-400 hover:text-red-600" data-testid={`alloc-remove-${idx}`}>
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>

                                    <div className="flex items-center justify-between pt-2 border-t">
                                        <div className="text-sm text-muted-foreground">
                                            <strong>{allocTotalVouchers}</strong> {t('vouchers.allocationSummary', { count: '' }).trim()} &middot;{' '}
                                            <strong className="text-emerald-600">${allocTotalValue}</strong> {t('vouchers.totalValue', { value: '' }).trim()}
                                        </div>
                                        <Button onClick={() => setAllocConfirmOpen(true)}
                                            className="bg-violet-600 hover:bg-violet-700" data-testid="alloc-save-btn">
                                            <Save className="w-4 h-4 mr-1" />
                                            {t('vouchers.saveAllocation')}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                        )}
                    </Card>
                </TabsContent>

                {/* ── TAB 2: STAFF INCENTIVES ── */}
                {canManageIncentives && (
                    <TabsContent value="incentives" className="space-y-6 mt-6" data-testid="incentives-panel">
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Coins className="w-4 h-4 text-blue-500" />
                                    {t('vouchers.incentiveConfig')}
                                </CardTitle>
                                <p className="text-xs text-muted-foreground">
                                    {t('vouchers.incentiveConfigHint')}
                                </p>
                            </CardHeader>
                            <CardContent>
                                {isSuperAdmin && organizations.length > 1 && (
                                    <div className="mb-4 space-y-1 max-w-sm">
                                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Building2 className="w-3 h-3" /> {t('vouchers.organization')}
                                        </Label>
                                        <Select value={incentiveOrgId} onValueChange={setIncentiveOrgId} disabled={!!scopeOrgId}>
                                            <SelectTrigger data-testid="incentive-org-select">
                                                <SelectValue placeholder={t('vouchers.selectOrganization')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {organizations.map(org => (
                                                    <SelectItem key={org.id} value={org.id}>
                                                        {org.name || org.display_name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                                    <div className="flex items-center justify-between sm:justify-start gap-3 p-3 rounded-lg border bg-muted/30" data-insight-id="vouchers.setting.incentives-enabled">
                                        <div>
                                            <Label className="text-sm font-medium">{t('vouchers.enableIncentives')}</Label>
                                            <p className="text-xs text-muted-foreground">{t('vouchers.enableIncentivesHint')}</p>
                                        </div>
                                        <Switch
                                            checked={incentiveEnabled}
                                            onCheckedChange={setIncentiveEnabled}
                                            data-testid="incentive-enabled-switch"
                                        />
                                    </div>
                                    <div className="space-y-1" data-insight-id="vouchers.setting.incentive-rate">
                                        <Label className="text-xs text-muted-foreground">{t('vouchers.ratePerTap')}</Label>
                                        <Input
                                            type="number" step="0.01" min="0" max="100"
                                            value={incentiveRate}
                                            onChange={(e) => setIncentiveRate(e.target.value)}
                                            placeholder="0.50"
                                            data-testid="incentive-rate-input"
                                        />
                                    </div>
                                    <Button
                                        onClick={saveIncentiveSettings}
                                        disabled={incentiveSaving}
                                        className="bg-emerald-600 hover:bg-emerald-700"
                                        data-testid="save-incentive-settings-btn"
                                    >
                                        {incentiveSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                                        {t('vouchers.saveSettings2')}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        <Card data-insight-id="vouchers.staff.overview-table">
                            <CardHeader className="pb-3 flex flex-row items-center justify-between">
                                <div>
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Users className="w-4 h-4 text-violet-500" />
                                        {t('vouchers.employeeOverview')}
                                    </CardTitle>
                                    <p className="text-xs text-muted-foreground">{t('vouchers.employeeOverviewHint')}</p>
                                </div>
                                <Button variant="outline" size="sm" onClick={loadStaffOverview} data-testid="refresh-staff-btn">
                                    <Activity className="w-4 h-4 mr-1" /> {t('vouchers.refresh')}
                                </Button>
                            </CardHeader>
                            <CardContent>
                                {staffLoading ? (
                                    <SkeletonList rows={5} />
                                ) : staffOverview.length === 0 ? (
                                    <div className="text-center py-10 text-muted-foreground">
                                        <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                        <p>{t('vouchers.noEmployeesAvail')}</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('vouchers.colEmployee')}</TableHead>
                                                <TableHead>{t('vouchers.colTotalTaps')}</TableHead>
                                                <TableHead>{t('vouchers.colBalance')}</TableHead>
                                                <TableHead>{t('vouchers.colTotalPaid')}</TableHead>
                                                <TableHead>{t('vouchers.colLastPayout')}</TableHead>
                                                <TableHead className="text-right">{t('vouchers.colAction')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {staffOverview.map(s => (
                                                <TableRow key={s.employee_id} data-testid={`staff-row-${s.employee_id}`}>
                                                    <TableCell className="font-medium">{s.employee_name}</TableCell>
                                                    <TableCell>{s.total_taps}</TableCell>
                                                    <TableCell className="font-semibold text-blue-600" data-testid={`staff-balance-${s.employee_id}`}>
                                                        {fmtMoney(s.incentive_balance)}
                                                    </TableCell>
                                                    <TableCell>{fmtMoney(s.total_payout_value)}</TableCell>
                                                    <TableCell className="text-xs text-muted-foreground">
                                                        {s.last_payout_date ? `${fmtMoney(s.last_payout_amount)} — ${parseUtc(s.last_payout_date).toLocaleDateString()}` : '-'}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            disabled={!s.incentive_balance || s.incentive_balance <= 0}
                                                            onClick={() => openRedeemModal(s)}
                                                            className="border-blue-500/40 text-blue-600 hover:bg-blue-500/10"
                                                            data-testid={`redeem-staff-btn-${s.employee_id}`}
                                                        >
                                                            <Wallet className="w-3.5 h-3.5 mr-1" /> {t('vouchers.redeemStaff')}
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                )}

                {/* ── TAB 3: VOUCHER STATISTICS ── */}
                {canViewStatistics && (
                    <TabsContent value="statistics" className="space-y-6 mt-6" data-testid="statistics-panel">
                        {/* Filters */}
                        <Card data-insight-id="vouchers.stats.filters">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <BarChart3 className="w-4 h-4 text-indigo-500" /> {t('vouchers.filters')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                                    <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground">{t('vouchers.startDate')}</Label>
                                        <Input type="date" value={fStart} onChange={(e) => setFStart(e.target.value)} data-testid="stats-filter-start" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground">{t('vouchers.endDate')}</Label>
                                        <Input type="date" value={fEnd} onChange={(e) => setFEnd(e.target.value)} data-testid="stats-filter-end" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground">{t('vouchers.location')}</Label>
                                        <Select value={fLocation} onValueChange={setFLocation}>
                                            <SelectTrigger data-testid="stats-filter-location"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">{t('vouchers.allLocations')}</SelectItem>
                                                {allLocations.map(l => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground">{t('vouchers.staff')}</Label>
                                        <Select value={fEmployee} onValueChange={setFEmployee}>
                                            <SelectTrigger data-testid="stats-filter-employee"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">{t('vouchers.allStaff')}</SelectItem>
                                                {(stats?.staff || []).map(s => (<SelectItem key={s.employee_id} value={s.employee_id}>{s.employee_name}</SelectItem>))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground">{t('vouchers.reasonColor')}</Label>
                                        <Select value={fReason} onValueChange={setFReason}>
                                            <SelectTrigger data-testid="stats-filter-reason"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">{t('vouchers.allReasons')}</SelectItem>
                                                <SelectItem value="late_food">Red — {t('staffMenu.lateFoodReason')}</SelectItem>
                                                <SelectItem value="wrong_order">Red — {t('staffMenu.wrongOrderReason')}</SelectItem>
                                                <SelectItem value="long_wait">Red — {t('staffMenu.longWaitReason')}</SelectItem>
                                                <SelectItem value="manager_goodwill">Red — {t('staffMenu.managerGoodwillReason')}</SelectItem>
                                                <SelectItem value="birthday">Green — {t('staffMenu.birthdayReason')}</SelectItem>
                                                <SelectItem value="other">Green — {t('staffMenu.otherReason')}</SelectItem>
                                                <SelectItem value="vip">Gold — {t('staffMenu.vipReason')}</SelectItem>
                                                <SelectItem value="staff_incentive">Blue — Staff Incentive</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-end gap-2">
                                        <Button variant="outline" size="sm" onClick={() => handleStatsExport('csv')} data-testid="stats-export-csv-btn">
                                            <Download className="w-3.5 h-3.5 mr-1" /> CSV
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => handleStatsExport('xlsx')} data-testid="stats-export-xlsx-btn">
                                            <Download className="w-3.5 h-3.5 mr-1" /> Excel
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {statsLoading ? (
                            <SkeletonList rows={6} />
                        ) : !stats ? (
                            <p className="text-sm text-muted-foreground text-center py-10">{t('vouchers.noDataStats')}</p>
                        ) : (
                            <>
                                {/* Current totals */}
                                <div data-insight-id="vouchers.stats.current-totals">
                                    <h3 className="text-sm font-semibold text-muted-foreground mb-2 uppercase tracking-wide">{t('vouchers.currentTotals')}</h3>
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                        <StatCard icon={<CircleDollarSign className="w-4 h-4 text-emerald-500" />}
                                            label={t('vouchers.outstandingVouchers')} value={stats.current_totals.outstanding_count}
                                            sub={fmtMoney(stats.current_totals.outstanding_value)}
                                            testid="stat-outstanding-vouchers" />
                                        <StatCard icon={<Wallet className="w-4 h-4 text-blue-500" />}
                                            label={t('vouchers.activeStaffBalance')} value={fmtMoney(stats.current_totals.active_staff_balance)}
                                            sub={`${stats.current_totals.active_staff_count} ${t('vouchers.activeStaff')}`}
                                            testid="stat-active-staff-balance" />
                                        <StatCard icon={<TrendingUp className="w-4 h-4 text-amber-500" />}
                                            label={t('vouchers.outstandingValue')} value={fmtMoney(stats.current_totals.outstanding_value)}
                                            sub={t('vouchers.unredeemed')}
                                            testid="stat-outstanding-value" />
                                        <StatCard icon={<Users className="w-4 h-4 text-violet-500" />}
                                            label={t('vouchers.staffWithBalance')} value={stats.current_totals.active_staff_count}
                                            sub={t('vouchers.currentlyAccruing')}
                                            testid="stat-active-staff-count" />
                                    </div>
                                </div>

                                {/* Historical totals */}
                                <div data-insight-id="vouchers.stats.historical-and-locations">
                                    <h3 className="text-sm font-semibold text-muted-foreground mb-2 uppercase tracking-wide">{t('vouchers.historicalTotals')}</h3>
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                        <StatCard icon={<Coins className="w-4 h-4 text-blue-500" />}
                                            label={t('vouchers.totalEarned')} value={fmtMoney(stats.historical_totals.total_incentives_earned)}
                                            sub={t('vouchers.paidPlusBalance')}
                                            testid="stat-total-earned" />
                                        <StatCard icon={<Wallet className="w-4 h-4 text-emerald-500" />}
                                            label={t('vouchers.totalPaid')} value={fmtMoney(stats.historical_totals.total_incentives_paid)}
                                            sub={t('vouchers.blueIssued')}
                                            testid="stat-total-paid" />
                                        <StatCard icon={<Gift className="w-4 h-4 text-violet-500" />}
                                            label={t('vouchers.vouchersIssuedCount')} value={stats.historical_totals.total_vouchers_issued}
                                            sub={fmtMoney(stats.historical_totals.total_issued_value)}
                                            testid="stat-total-issued" />
                                        <StatCard icon={<Check className="w-4 h-4 text-amber-500" />}
                                            label={t('vouchers.vouchersRedeemedCount')} value={stats.historical_totals.total_vouchers_redeemed}
                                            sub={fmtMoney(stats.historical_totals.total_redeemed_value)}
                                            testid="stat-total-redeemed" />
                                    </div>
                                </div>

                                {/* Location drill-down */}
                                <Card>
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-indigo-500" /> {t('vouchers.locationBreakdown')}
                                        </CardTitle>
                                        <p className="text-xs text-muted-foreground">{t('vouchers.clickRowDetails')}</p>
                                    </CardHeader>
                                    <CardContent>
                                        {stats.locations.length === 0 ? (
                                            <p className="text-sm text-muted-foreground text-center py-6">{t('vouchers.noDataStats')}</p>
                                        ) : (
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead className="w-8"></TableHead>
                                                        <TableHead>{t('vouchers.location2')}</TableHead>
                                                        <TableHead>{t('vouchers.issued')}</TableHead>
                                                        <TableHead>{t('vouchers.redeemed2')}</TableHead>
                                                        <TableHead>{t('vouchers.outstanding')}</TableHead>
                                                        <TableHead>{t('vouchers.staffPaid')}</TableHead>
                                                        <TableHead>{t('vouchers.staffBalance')}</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {stats.locations.map(loc => {
                                                        const isOpen = expandedLocs[loc.location_id];
                                                        const locStaff = staffByLoc(loc.location_id);
                                                        return (
                                                            <Fragment key={loc.location_id}>
                                                                <TableRow
                                                                    className="cursor-pointer hover:bg-muted/30"
                                                                    onClick={() => toggleLoc(loc.location_id)}
                                                                    data-testid={`stats-loc-row-${loc.location_id}`}>
                                                                    <TableCell>
                                                                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                                    </TableCell>
                                                                    <TableCell className="font-medium">{loc.location_name}</TableCell>
                                                                    <TableCell>{loc.issued_count} · {fmtMoney(loc.issued_value)}</TableCell>
                                                                    <TableCell className="text-emerald-600">{loc.redeemed_count} · {fmtMoney(loc.redeemed_value)}</TableCell>
                                                                    <TableCell className="text-amber-600">{loc.outstanding_count} · {fmtMoney(loc.outstanding_value)}</TableCell>
                                                                    <TableCell className="text-blue-600">{loc.staff_paid_count} · {fmtMoney(loc.staff_paid_value)}</TableCell>
                                                                    <TableCell className="font-semibold text-blue-600">{fmtMoney(loc.staff_outstanding_balance)}</TableCell>
                                                                </TableRow>
                                                                {isOpen && (
                                                                    <TableRow className="bg-muted/20">
                                                                        <TableCell colSpan={7}>
                                                                            {locStaff.length === 0 ? (
                                                                                <p className="text-xs text-muted-foreground py-2">{t('vouchers.noStaffAtLoc')}</p>
                                                                            ) : (
                                                                                <Table>
                                                                                    <TableHeader>
                                                                                        <TableRow>
                                                                                            <TableHead>{t('vouchers.staff')}</TableHead>
                                                                                            <TableHead>{t('vouchers.taps')}</TableHead>
                                                                                            <TableHead>{t('vouchers.earned')}</TableHead>
                                                                                            <TableHead>{t('vouchers.paid')}</TableHead>
                                                                                            <TableHead>{t('vouchers.balance')}</TableHead>
                                                                                            <TableHead>{t('vouchers.lastPayoutCol')}</TableHead>
                                                                                        </TableRow>
                                                                                    </TableHeader>
                                                                                    <TableBody>
                                                                                        {locStaff.map(s => (
                                                                                            <TableRow key={s.employee_id} data-testid={`stats-staff-row-${s.employee_id}`}>
                                                                                                <TableCell className="font-medium">{s.employee_name}</TableCell>
                                                                                                <TableCell>{s.tap_count}</TableCell>
                                                                                                <TableCell>{fmtMoney(s.earned_value)}</TableCell>
                                                                                                <TableCell>{fmtMoney(s.paid_value)}</TableCell>
                                                                                                <TableCell className="font-semibold text-blue-600">{fmtMoney(s.incentive_balance)}</TableCell>
                                                                                                <TableCell className="text-xs text-muted-foreground">
                                                                                                    {s.last_payout_date ? `${fmtMoney(s.last_payout_amount)} — ${parseUtc(s.last_payout_date).toLocaleDateString()}` : '-'}
                                                                                                </TableCell>
                                                                                            </TableRow>
                                                                                        ))}
                                                                                    </TableBody>
                                                                                </Table>
                                                                            )}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                )}
                                                            </Fragment>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        )}
                                    </CardContent>
                                </Card>
                            </>
                        )}
                    </TabsContent>
                )}

                {/* ── TAB 4: SCHEDULED REPORTS ── */}
                {canViewStatistics && (
                    <TabsContent value="audit_reports" className="space-y-6 mt-6" data-testid="audit-reports-panel">
                        <VoucherAuditReports />
                        <VoucherActivityLog organizationId={settingsOrgId} />
                    </TabsContent>
                )}

                {/* ── TAB 5: RECIPIENTS (v4.45) ── */}
                <TabsContent value="recipients" className="space-y-6 mt-6" data-testid="recipients-panel">
                    <VoucherRecipientsTab
                        voucherConfigs={lrSourceVouchers}
                        organizations={organizations}
                        allLocations={allLocations}
                        token={token}
                        deepLink={deepLinkParams}
                        onDeepLinkConsumed={() => setDeepLinkParams(null)}
                    />
                </TabsContent>
            </Tabs>

            {/* Brand library picker — shared by every image field on this page */}
            <BrandAssetPicker
                open={!!libraryPicker}
                onOpenChange={(v) => { if (!v) setLibraryPicker(null); }}
                kind={libraryPicker?.kind}
                orgId={settingsOrgId}
                token={token}
                isSuperAdmin={user?.role === 'super_admin'}
                onApply={(url, asset) => libraryPicker?.apply?.(url, asset)}
            />

            {/* Allocation Confirmation Dialog */}
            <Dialog open={allocConfirmOpen} onOpenChange={setAllocConfirmOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('vouchers.confirmAllocation')}</DialogTitle>
                        <DialogDescription>{t('vouchers.aboutToAllocate')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 py-2" data-testid="alloc-confirm-summary">
                        {allocList.map((item, idx) => (
                            <div key={item._key || `alloc-confirm-${idx}`} className="flex justify-between text-sm">
                                <span className="font-medium">
                                    {item.recipient_name}
                                    <span className="text-muted-foreground text-xs ml-1">
                                        ({methodLabels[item.delivery_method] || t('vouchers.staffCard')})
                                    </span>
                                </span>
                                <span>{item.quantity} x ${item.value}</span>
                            </div>
                        ))}
                        <div className="border-t pt-2 flex justify-between font-semibold">
                            <span>{t('vouchers.total')}</span>
                            <span className="text-emerald-600">{allocTotalVouchers} {t('vouchers.allocationSummary', { count: '' }).trim()} &middot; ${allocTotalValue}</span>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAllocConfirmOpen(false)} disabled={allocating}>
                            {t('common.no')}
                        </Button>
                        <Button className="bg-violet-600 hover:bg-violet-700" onClick={submitAllocation} disabled={allocating} data-testid="alloc-confirm-yes">
                            {allocating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                            {t('common.yes')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Redeem Staff Modal */}
            <Dialog open={!!redeemTarget} onOpenChange={(o) => { if (!o) { setRedeemTarget(null); setRedeemAmount(''); } }}>
                <DialogContent data-testid="redeem-staff-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Wallet className="w-5 h-5 text-blue-500" /> {t('vouchers.redeemTitle')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('vouchers.redeemHint')}
                        </DialogDescription>
                    </DialogHeader>
                    {redeemTarget && (
                        <div className="space-y-4">
                            <div className="rounded-lg border p-3 bg-muted/30 space-y-1">
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">{t('vouchers.employee')}</span>
                                    <span className="font-medium" data-testid="redeem-employee-name">{redeemTarget.employee_name}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">{t('vouchers.currentBalance')}</span>
                                    <span className="font-semibold text-blue-600" data-testid="redeem-current-balance">{fmtMoney(redeemTarget.incentive_balance)}</span>
                                </div>
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">{t('vouchers.payoutAmount')}</Label>
                                <Input
                                    type="number" step="0.01" min="0.01" max={redeemTarget.incentive_balance}
                                    value={redeemAmount}
                                    onChange={(e) => setRedeemAmount(e.target.value)}
                                    placeholder={String(redeemTarget.incentive_balance)}
                                    data-testid="redeem-amount-input"
                                />
                                <p className="text-xs text-muted-foreground">{t('vouchers.maxAmount', { amount: fmtMoney(redeemTarget.incentive_balance) })}</p>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setRedeemTarget(null); setRedeemAmount(''); }} disabled={redeeming} data-testid="redeem-cancel-btn">
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={confirmRedeem}
                            disabled={redeeming || !redeemAmount || parseFloat(redeemAmount) <= 0}
                            data-testid="redeem-confirm-btn"
                        >
                            {redeeming ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wallet className="w-4 h-4 mr-1" />}
                            {t('vouchers.confirmRedemption')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function StatCard({ icon, label, value, sub, testid }) {
    return (
        <Card className="yp-elevate" data-testid={testid}>
            <CardContent className="p-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                    {icon} {label}
                </div>
                <p className="text-2xl font-bold text-foreground">{value}</p>
                {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
            </CardContent>
        </Card>
    );
}
